import { Component, OnInit, OnDestroy, HostListener } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { MessageService } from 'primeng/api';
import { catchError, firstValueFrom, forkJoin, of, Subscription } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { CondicaoDePagamento } from '../../../enums/condicaoDePagamento.enum';
import { Moeda } from '../../../enums/moeda.enum';
import { TipoDeDesconto } from '../../../enums/TipoDeDesconto.enum';
import { TipoDeImposto } from '../../../enums/tipoDeImposto.enum';
import { TipoItem } from '../../../enums/TipoItem.enum';
import { TipoPadraoNota } from '../../../enums/TipoPadraoNota.enum';
import { PessoaDTO } from '../../../models/clientes/pessoa.model';
import { TipoTransacao } from '../../../enums/TipoTransacao.enum';
import { environment } from '../../../environments/environment';
import { TimbradoDTO } from '../../../models/configuracoes/timbrao.model';
import { ItemDTO } from '../../../models/item.model';
import { ItemDeNotaDTO, NotaDTO, ResumoCancelamentoNotaDTO, TituloDTO } from '../../../models/nota.model';
import { TipoDeCobrancaDTO } from '../../../models/dashboard/titulo.model';
import { TimbradoService } from '../../../services/configuracoes/timbrado.service';
import { ItemsService } from '../../../services/financeiro/item.service';
import { NotaService } from '../../../services/financeiro/notas.service';
import { ContaService } from '../../../services/conta/conta.service';
import { EmpresaConfigService } from '../../../services/configuracoes/empresa.service';
import { CotacaoService } from '../../../services/cotacao/cotacao.service';
import { PessoaService } from '../../../services/pessoas/pessoas.service';
import { EstadoNotaEletronica } from '../../../enums/EstadoNotaEletronica.enum';
import { TranslateService } from '@ngx-translate/core';
import { NfeService } from '../../../services/financeiro/nfe.service';
import { EventoEnvioNfe, NfeImpressaoDTO, NfeRespostaEventoEnvioDTO } from '../../../models/nfe.model';
import { FormaPagamentoResult, DadosPagamentoDinheiro, DadosPagamentoConta } from '../forma-pagamento-dialog/forma-pagamento-dialog.interfaces';
import { converterDireto, ConversaoNaoPermitidaError } from '../../utils/moeda-conversao.util';
import { PermissaoService } from '../../../services/permissao/permissao.service';
import { ContadorContextoService } from '../../../services/contador/contador-contexto.service';
import { moedasCompativeis, enumMoedaParaIso, podeConverterEntre } from '../../../utils/moeda-pareamento.util';
import { observacaoItemParaExibicao } from '../../utils/observacao-item.util';

interface ItemDevolucao extends ItemDTO {
  valorUnitario: number,
  quantidade: number,
  quantidadeMaxima: number,
}

interface DadosNotaEmFatura {
  numeroFilial?: string;
  pontoDeExpedicao?: string;
  proximoNumero?: string;
}

// Interfaces para agrupamento de erros de transmissao NFe
interface ErroNfeItem {
  campo: string;
  mensagem: string;
}

interface AcaoErroNfe {
  tipo: 'nova-aba' | 'scroll';
  rota?: string;
  queryParams?: Record<string, string>;
  sectionId?: string;
  labelKey: string;
  icone: string;
}

interface GrupoErroNfe {
  grupo: string;
  titulo: string;
  erros: ErroNfeItem[];
  acoes: AcaoErroNfe[];
}

// Campos que pertencem a aba Atividade Economica (indice 2) no configuracao-da-empresa
const CAMPOS_ATIVIDADE_ECONOMICA = [
  'Atividade_economica_nao_encontrada',
];

// Campo especial de timbrado que deve fazer scroll na pagina
const CAMPO_NUMERO_FATURA = 'numero_fatura_not_null';

enum TipoDeDevolucao {
  EFETIVO = "efetivo",
  CONTA = "conta",
}

@Component({
  selector: 'app-fatura-view',
  templateUrl: './fatura-view.component.html',
  styleUrl: './fatura-view.component.scss'
})
export class FaturaViewComponent implements OnInit, OnDestroy {

  // Constantes e variaveis de ambiente
  env = environment;
  private readonly IMPRESSAO_PADRAO = 'A4';
  private readonly PROGRESS_BAR_STEP_DELAY = 500; // ms

  // identificadores
  notaId?: number;
  idEmpresa: string = '';

  // Enums e tipos fixos
  moedaDolar: Moeda = Moeda.DOLAR;
  moedaGuarani: Moeda = Moeda.GUARANI;
  moedaReal: Moeda = Moeda.REAL;
  moedaPeso: Moeda = Moeda.PESO;

  /**
   * Moedas visiveis no bloco "Recebido em" da fatura, derivadas da matriz de
   * pareamento da moeda padrao da empresa (moedasCompativeis).
   * Inicializada com fallback seguro Set([GUARANI]) para evitar flash visual de
   * 4 cards durante o carregamento assincrono de carregarMoedaPadrao().
   * Sera recalculada ao final de carregarMoedaPadrao().
   */
  moedasVisiveis: Set<Moeda> = new Set([Moeda.GUARANI]);

  // Formularios
  tipoDeImpressaoForm!: FormGroup;
  tipoDeDevolucaoForm!: FormGroup;

  // Modelos e entidades
  form: NotaDTO = this.createEmptyForm();
  itensDeNota: ItemDTO[] = [];
  itensDeNotaDevolucao: ItemDevolucao[] = [];
  timbradosVendaList: TimbradoDTO[] = [];// aqui
  timbradosDevolucaoList: TimbradoDTO[] = [];
  timbradoAtual?: TimbradoDTO;
  selectedTimbrado?: TimbradoDTO;
  novoDadosNotaEmFaturaCompra: DadosNotaEmFatura = {
    pontoDeExpedicao: '',
    numeroFilial: '000',
    proximoNumero: '0000000',
  };
  numeroFaturaNovo?: String;

  // visibilidade de dialogos
  dialogoImpressaoVisivel: boolean = false;
  dialogoFaturaLegalVisivel: boolean = false;
  dialogoEditarFaturaLegalVisivel: boolean = false;
  dialogoDevolverEtapa1Visivel: boolean = false;
  dialogoDetalhesFaturaVisivel: boolean = false;
  dialogoHistoricoEventosVisivel: boolean = false;
  dialogoMotivoCancelamentoVisivel: boolean = false;
  dialogoMotivoInutilizacaoVisivel: boolean = false;
  dialogoConfirmarTransmissaoVisivel: boolean = false;
  dialogoImpressaoNfeVisivel: boolean = false;

  // Flags de loading para botões
  isTransmitindo: boolean = false;
  isCancelando: boolean = false;
  isInutilizando: boolean = false;
  isConsultando: boolean = false;
  isImprimindoNfe: boolean = false;
  isImprimindoFatura: boolean = false;
  isImprimindoFicha: boolean = false;

  /**
   * Flag da empresa "Imprimir ficha do evento" (espelha deveImprimirFicha do mobile).
   * Carregada em carregarMoedaPadrao() a partir da configuracao da empresa.
   * Controla a exibicao do botao "Imprimir Ficha" junto de ehTipoDeTransacaoVenda.
   */
  deveImprimirFicha: boolean = false;

  // Dialogo de status de operacao NFe
  dialogoOperacaoNfeVisivel: boolean = false;
  operacaoNfeEstado: 'processando' | 'sucesso' | 'erro' = 'processando';
  operacaoNfeTipo: 'transmissao' | 'cancelamento' | 'consulta' | 'inutilizacao' = 'transmissao';
  operacaoNfeMensagemErro: string = '';
  operacaoNfeProgresso: number = 0;

  // Erros agrupados para o dialog de transmissao NFe
  gruposErroNfe: GrupoErroNfe[] = [];
  temErrosAgrupados: boolean = false;

  // Flags de tipo de transação
  ehTipoDeTransacaoVenda = false;
  ehTipoDeTransacaoCompra = false;
  ehTipoDeTransacaoDevolucao = false;
  ehTipoDeTransacaoDevolucaoDeCompra = false;

  // Opcao de impressao automatica da empresa (devolucoes)
  private tipoImprimir: string = 'PERGUNTAR';
  private pendingPrintAction: string | null = null;

  // Componente de forma de pagamento (devolucao)
  mostrarDialogPagamentoDevolucao = false;
  moedaPadraoISO: 'BRL' | 'PYG' | 'USD' | 'ARS' = 'PYG';
  cotUSD = 1;
  cotPYG = 7180;
  cotBRL = 5.47;
  /** Cotacao Peso Argentino em Guaranis (1 ARS = N PYG). 0 = peso desabilitado. */
  cotPesoGuarani = 0;
  /** Cotacao Real em Guaranis (1 BRL = N PYG). Usada na conversao direta PYG<->BRL. */
  cotRealGuarani = 0;
  contasFormatadas: { label: string; value: any }[] = [];
  processandoDevolucao = false;

  // Outros auxiliares
  pdf?: string;
  motivoCancelamentoNfe: string = '';
  /**
   * Qual cancelamento o dialogo de motivo vai executar ao confirmar:
   * 'nfe' = cancelamento fiscal (`/nfe/cancelar`); 'nota' = so no sistema (`/nota/cancelar`).
   */
  tipoCancelamentoSolicitado: 'nfe' | 'nota' = 'nfe';

  /**
   * O que sera estornado se a nota for cancelada — parcelas ja pagas e movimentos gerados.
   *
   * Preenchido ao abrir o dialogo de cancelamento. Com `possuiPagamento` verdadeiro o dialogo
   * mostra o aviso de estorno e exige o aceite explicito: cancelar aqui devolve dinheiro que ja
   * entrou no caixa, e o usuario nao pode descobrir isso depois.
   */
  resumoCancelamento: ResumoCancelamentoNotaDTO | null = null;
  carregandoResumoCancelamento: boolean = false;
  estornoDePagamentosAceito: boolean = false;
  motivoInutilizacaoNfe: string = '';


  estadoNotaEletronica: { value: EstadoNotaEletronica; label: string } = { value: EstadoNotaEletronica.EM_DEFINICAO, label: '' };
  estadoNfeCarregado: boolean = false;
  // MEDIO-03: flag de loading enquanto consulta SIFEN para evitar UI desatualizada
  carregandoEstadoNfe: boolean = false;

  faturaEletronica?: NfeRespostaEventoEnvioDTO;

  historicoEventosEnvioNfe: EventoEnvioNfe[] = [];

  constructor(
    private translate: TranslateService,
    private notaService: NotaService,
    private nfeService: NfeService,
    private timbradoService: TimbradoService,
    private itemsService: ItemsService,
    private messageService: MessageService,
    private route: ActivatedRoute,
    private router: Router,
    private fb: FormBuilder,
    private contaService: ContaService,
    private empresaConfigService: EmpresaConfigService,
    private cotacaoService: CotacaoService,
    private pessoaService: PessoaService,
    private permissaoService: PermissaoService,
    private contadorContextoService: ContadorContextoService,
  ) { }

  /**
   * Permissao para cancelar ESTA nota.
   *
   * Regra: quem enxerga a operacao pode desfazê-la. Venda e devolucao de venda exigem acesso
   * a Vendas; compra e devolucao de compra, acesso a Compras. Os demais tipos (transferencia,
   * cambio, ajustes de estoque) seguem exigindo a permissao especifica de cancelar nota.
   *
   * E a mesma regra aplicada pelo backend em `NotaService.cancelarNota` — manter as duas
   * alinhadas, senao o botao aparece e a API recusa (ou o contrario).
   */
  get podeCancelarNotaPermissao(): boolean {
    switch (this.form?.tipoDeTransacao) {
      case TipoTransacao.VENDA:
      case TipoTransacao.DEVOLUCAO:
        return this.permissaoService.temPermissao('pontoDeVenda');
      case TipoTransacao.COMPRA:
      case TipoTransacao.DEVOLUCAO_DE_COMPRA:
        return this.permissaoService.temPermissao('compra');
      default:
        return this.permissaoService.temPermissao('cancelarNota');
    }
  }

  /** Flag de permissao para cancelar movimento */
  podeCancelarMovimentoPermissao: boolean = true;

  private langChangeSub!: Subscription;

  ngOnInit(): void {
    this.podeCancelarMovimentoPermissao = this.permissaoService.temPermissao('cancelarMovimento');

    const idParam = this.route.snapshot.paramMap.get('id');
    if (idParam) {
      this.notaId = parseInt(idParam, 10);
      if (!isNaN(this.notaId)) {
        this.loadNota(this.notaId);
      }
    }

    // this.consultarNfe();

    this.tipoDeImpressaoForm = this.fb.group({
      impressao: [this.IMPRESSAO_PADRAO, Validators.required]
    });

    this.tipoDeDevolucaoForm = this.fb.group({
      tipoDeDevolucao: [TipoDeDevolucao.EFETIVO, Validators.required]
    });

    this.idEmpresa = sessionStorage.getItem('id') || '';

    this.carregarContas();
    this.carregarMoedaPadrao(); // carregarCotacoes() é chamado dentro, apos moeda ser definida

    this.buildEstadoNotaEletronica();
    // Atualiza labels quando idioma muda
    this.langChangeSub = this.translate.onLangChange.subscribe((event) => {
      this.buildEstadoNotaEletronica();
    });
  }

  ngOnDestroy(): void {
    if (this.langChangeSub) {
      this.langChangeSub.unsubscribe();
    }
  }

  private buildEstadoNotaEletronica() {
    this.estadoNotaEletronica = {
      value: EstadoNotaEletronica.EM_DEFINICAO,
      label: this.translate.instant(`enums.estado_nota_eletronica.${EstadoNotaEletronica.EM_DEFINICAO.toLowerCase()}`)
    };
  }

  loadNota(id: number): void {
    this.notaId = id;
    this.notaService.get(`/nota/${id}`).subscribe({
      next: (response: any) => {
        console.log('Resposta da nota carregada:', response);

        this.form = this.mapNotaResponse(response);
        this.loadItensDeNota(this.form.itemDeNota);
        this.setTipoDeTransacaoFlags(this.form.tipoDeTransacao);
        this.loadTimbradoSeNecessario();
        this.numeroFaturaNovo = String(this.form.numeroFatura).padStart(7, '0');

        // Inicializar o estado da nota eletronica a partir do valor persistido no banco.
        // O campo pode vir como string direta (ex: "AUTORIZADA") ou como objeto com .enum.
        // Isso garante que o badge e os botoes sejam exibidos corretamente de imediato,
        // sem depender da consulta SIFEN assincrona que pode demorar ou falhar.
        const estadoBanco: string | null = response.estadoNotaEletronica?.enum ?? response.estadoNotaEletronica ?? null;
        if (estadoBanco) {
          this.atualizarEstadoUI(estadoBanco, null);
          this.estadoNfeCarregado = true;
        }

        // MEDIO-03: ativa flag de loading antes da consulta SIFEN assincrona.
        // O estado do banco ja foi aplicado acima como fallback imediato.
        // Quando consultarNfe() retornar, sobrescreve com o estado real do SIFEN.
        if (this.form.ehFaturaLegal && this.form.tipoNota === TipoPadraoNota.ELETRONICA) {
          this.carregandoEstadoNfe = true;
          this.consultarNfe().finally(() => { this.carregandoEstadoNfe = false; });
        }

        // Executar acao pendente de impressao apos devolucao (se houver)
        if (this.pendingPrintAction) {
          const action = this.pendingPrintAction;
          this.pendingPrintAction = null;
          switch (action) {
            case 'IMPRIMIR':
              setTimeout(() => this.imprimirAutomaticoViaDevolucao(), 500);
              break;
            case 'PERGUNTAR':
            case 'NADA_A_FAZER':
              this.dialogoImpressaoVisivel = true;
              break;
          }
        }
      },

      error: (err) => {
        this.showError(this.translate.instant('fatura-view.mensagens.erro_carregar_fatura'));
      },
    });
  }

  loadItensDeNota(itensDeNota: ItemDeNotaDTO[]): void {
    if (!itensDeNota || itensDeNota.length === 0) {
      this.itensDeNota = [];
      return;
    }

    const requisicoes = itensDeNota.map(item => {
      // Extrai o item_id de forma robusta: pode vir como item_id direto ou como item.id (objeto aninhado)
      const itemId = item.item_id ?? (item as any).item?.id;
      return itemId
        ? this.itemsService.getById(`/item`, itemId.toString()).pipe(
            catchError(() => of(null)) // se falhar, mantém null para não quebrar o forkJoin
          )
        : of(null); // mantém o índice mesmo sem requisição para garantir a integridade da ordem
    });

    forkJoin(requisicoes).subscribe({
      next: (response: (ItemDTO | null)[]) => {
        // mantém os mesmos índices do form.itemDeNota
        this.itensDeNota = response.map(r => r ?? this.createEmptyItem());
      },
      error: (err) => {
        this.showError(this.translate.instant('fatura-view.mensagens.erro_carregar_itens'));
        this.itensDeNota = [];
      }
    });
  }

  private loadTimbrados(tipoDeTransacao: TipoTransacao): void {
    const url = `/timbrado/buscarPorTimbradoAtivoEValido?tipoDeTransacao=${tipoDeTransacao}`;
    this.timbradoService.get(url).subscribe({
      next: (response: any) => {
        const lista = (response?.content && Array.isArray(response.content))
          ? response.content.map((item: any) => this.mapTimbrado(item))
          : [];

        if (tipoDeTransacao == TipoTransacao.VENDA) {
          this.timbradosVendaList = lista;
        } else if (tipoDeTransacao == TipoTransacao.DEVOLUCAO) {
          this.timbradosDevolucaoList = lista;
        }
      },
      error: () => {
        if (tipoDeTransacao == TipoTransacao.VENDA) {
          this.timbradosVendaList = [];
        } else if (tipoDeTransacao == TipoTransacao.DEVOLUCAO) {
          this.timbradosDevolucaoList = [];
        }
      }
    });
  }

  private loadTimbradoPorId(idTimbrado: number): void {
    const url = `/timbrado/${idTimbrado}`;
    this.timbradoService.get(url).subscribe({
      next: (response: any) => {
        this.timbradoAtual = response;
      },
      error: () => {
        this.showError(this.translate.instant('fatura-view.mensagens.erro_carregar_timbrado'));
      }
    });
  }

  private setTipoDeTransacaoFlags(tipo: TipoTransacao): void {
    this.ehTipoDeTransacaoVenda = tipo === TipoTransacao.VENDA;
    this.ehTipoDeTransacaoCompra = tipo === TipoTransacao.COMPRA;
    this.ehTipoDeTransacaoDevolucao = tipo === TipoTransacao.DEVOLUCAO;
    this.ehTipoDeTransacaoDevolucaoDeCompra = tipo === TipoTransacao.DEVOLUCAO_DE_COMPRA;
  }

  private loadTimbradoSeNecessario(): void {
    if (this.form.ehFaturaLegal &&
      this.form.tipoDeTransacao !== TipoTransacao.COMPRA &&
      this.form.tipoDeTransacao !== TipoTransacao.DEVOLUCAO_DE_COMPRA) {
      this.loadTimbradoPorId(this.form.timbrado_id);
    }
  }

  private montarPayloadFatura(dados: {
    numeroFatura: number | undefined;
    numeroFilial: number | undefined;
    numeroPontoExpedicao: number | undefined;
    timbrado_id?: number | undefined;
    numeroTimbrado?: string | undefined;
  }): any {
    return {
      ehFaturaLegal: true,
      numeroFatura: dados.numeroFatura || undefined,
      numeroFilial: dados.numeroFilial || undefined,
      numeroPontoExpedicao: dados.numeroPontoExpedicao || undefined,
      observacao: '',
      timbrado_id: dados.timbrado_id || undefined,
      numeroTimbrado: dados.numeroTimbrado,
    };
  }

  private transformarNota(url: string, payload: any, onSuccessMessage: string): void {
    this.notaService.transformarNotaEmFatura(url, payload).subscribe({
      next: (response: any) => {
        if (response) this.timbradoAtual = response;
        this.showSuccess(onSuccessMessage);
        this.dialogoFaturaLegalVisivel = false;
        this.dialogoEditarFaturaLegalVisivel = false;
        this.loadNota(this.form.id);
      },
      error: (err) => {
        const msg = err?.error?.message || this.translate.instant('fatura-view.mensagens.erro_transformar_fatura');
        this.showError(msg);
      }
    });
  }

  private editarNotaEmFatura(timbrado: TimbradoDTO): void {
    if (!timbrado.id && !this.numeroFaturaNovo) {
      this.showError(this.translate.instant('fatura-view.mensagens.erro_atualizar_timbrado'));
      return;
    }

    const payload = this.montarPayloadFatura({
      numeroFatura: isNaN(Number(this.numeroFaturaNovo)) ? undefined : Number(this.numeroFaturaNovo),
      numeroFilial: timbrado.numeroFilial,
      numeroPontoExpedicao: timbrado.pontoDeExpedicao,
      timbrado_id: timbrado.id,
      numeroTimbrado: timbrado.timbrado,
    });

    const url = `/nota/transformar-nota-em-fatura/${this.form.id}`;
    this.transformarNota(url, payload, this.translate.instant('fatura-view.mensagens.operacao_sucesso'));
  }

  atualizarTimbrado(): void {
    if (this.notaCancelada) {
      this.avisarNotaCancelada();
      return;
    }

    if (this.timbradoAtual) {
      this.editarNotaEmFatura(this.timbradoAtual);
      return;
    }

    if (this.form.tipoDeTransacao === TipoTransacao.COMPRA && this.numeroFaturaNovo) {
      if (!isNaN(Number(this.numeroFaturaNovo))) {
        this.editarNumeroFaturaEmNotaCompra(Number(this.numeroFaturaNovo));
      }
    }
  }

  transformaNotaEmFatura(): void {
    if (this.notaCancelada) {
      this.avisarNotaCancelada();
      return;
    }

    const dados = this.novoDadosNotaEmFaturaCompra;
    const timbrado = this.selectedTimbrado;

    // Se for compra/devolução de compra, usa os dados manuais
    if (this.ehTipoDeTransacaoCompra || this.ehTipoDeTransacaoDevolucaoDeCompra) {
      if (dados.proximoNumero && dados.numeroFilial && dados.pontoDeExpedicao) {
        this.transformaNotaCompraEmFatura();
        return;
      }
      this.showError(this.translate.instant('fatura-view.mensagens.preencha_campos_fatura'));
      return;
    }

    // Para venda/devolução de venda, precisa de timbrado
    if (!timbrado) {
      this.showError(this.translate.instant('fatura-view.mensagens.selecione_timbrado'));
      return;
    }

    const payload = this.montarPayloadFatura({
      numeroFatura: timbrado.proximoNumero,
      numeroFilial: timbrado.numeroFilial,
      numeroPontoExpedicao: timbrado.pontoDeExpedicao,
      timbrado_id: timbrado.id,
      numeroTimbrado: timbrado.timbrado,
    });

    this.transformarNota(`/nota/transformar-nota-em-fatura/${this.form.id}`, payload, this.translate.instant('fatura-view.mensagens.transformado_fatura_legal'));
  }

  transformaNotaCompraEmFatura(): void {
    const dados = this.novoDadosNotaEmFaturaCompra;
    const payload = this.montarPayloadFatura({
      numeroFatura: isNaN(Number(dados.proximoNumero)) ? undefined : Number(dados.proximoNumero),
      numeroFilial: isNaN(Number(dados.numeroFilial)) ? undefined : Number(dados.numeroFilial),
      numeroPontoExpedicao: isNaN(Number(dados.pontoDeExpedicao)) ? undefined : Number(dados.pontoDeExpedicao),
    });

    this.transformarNota(`/nota/transformar-nota-em-fatura/${this.form.id}`, payload, this.translate.instant('fatura-view.mensagens.transformado_fatura_legal'));
  }

  editarNumeroFaturaEmNotaCompra(novoNumeroFatura: number): void {
    const payload = this.montarPayloadFatura({
      numeroFatura: novoNumeroFatura,
      numeroFilial: this.form.numeroFilial,
      numeroPontoExpedicao: this.form.numeroPontoExpedicao,
    });

    this.transformarNota(`/nota/transformar-nota-em-fatura/${this.form.id}`, payload, this.translate.instant('fatura-view.mensagens.transformado_fatura_legal'));
  }

  /** Abre o dialogo de forma de pagamento para devolucao (etapa 2) */
  async devolver(): Promise<void> {
    // Verificar se ha itens com quantidade > 0 para devolver
    const temItensParaDevolver = this.itensDeNotaDevolucao.some(i => i.quantidade > 0);
    if (!temItensParaDevolver) {
      this.showError(this.translate.instant('fatura.messages.selecione_itens_devolucao'));
      return;
    }

    // Validar timbrado obrigatorio para devolucao de fatura legal de venda
    if (this.form.ehFaturaLegal && this.ehTipoDeTransacaoVenda && !this.selectedTimbrado) {
      this.showError(this.translate.instant('fatura.messages.timbrado_devolucao_obrigatorio'));
      return;
    }

    // Validar dados do cliente para nota eletronica na devolucao
    if (this.selectedTimbrado) {
      const tipoNota = this.selectedTimbrado.tipoNota;
      if (tipoNota === TipoPadraoNota.ELETRONICA) {
        try {
          const pessoa: PessoaDTO = await firstValueFrom(
            this.pessoaService.getById('/pessoas', String(this.form.pessoa_id))
          );
          const camposFaltantes: string[] = [];
          if (!pessoa.ruc) {
            camposFaltantes.push(this.translate.instant('vendas.mensagens.campo_ruc'));
          }
          if (!pessoa.endereco) {
            camposFaltantes.push(this.translate.instant('vendas.mensagens.campo_endereco'));
          }
          if (!pessoa.cidade_id && !(pessoa as any).cidade) {
            camposFaltantes.push(this.translate.instant('vendas.mensagens.campo_cidade'));
          }
          if (camposFaltantes.length > 0) {
            const listaCampos = camposFaltantes.join(', ');
            this.messageService.add({
              severity: 'warn',
              summary: this.translate.instant('common.atencao'),
              detail: this.translate.instant('vendas.mensagens.dados_cliente_nfe', { campos: listaCampos }),
              life: 8000,
            });
            return;
          }
        } catch {
          this.showError(this.translate.instant('fatura-view.mensagens.erro_carregar_cliente'));
          return;
        }
      }
    }

    // Fechar dialogo etapa 1 e abrir forma de pagamento
    this.dialogoDevolverEtapa1Visivel = false;
    this.mostrarDialogPagamentoDevolucao = true;
  }

  carregarContas(): void {
    this.contaService.get('/conta?tipoConta=BANCO').subscribe({
      next: (res) => {
        if (res?.content && Array.isArray(res.content)) {
          this.contasFormatadas = res.content.map((conta: any) => ({ label: conta.tituloDaConta, value: conta }));
        } else {
          this.contasFormatadas = [];
        }
      },
      error: () => (this.contasFormatadas = []),
    });
  }

  carregarMoedaPadrao(): void {
    if (!this.idEmpresa) return;
    this.empresaConfigService.get(`/configuracaoEmpresa/${this.idEmpresa}`).subscribe({
      next: (res) => {
        const moeda = res?.moeda;
        const codigoOperacao =
          moeda?.codigoOperacao ??
          (moeda?.enum === 'REAL'  ? 'BRL'
           : moeda?.enum === 'DOLAR' ? 'USD'
           : moeda?.enum === 'PESO'  ? 'ARS'
           : 'PYG');
        this.moedaPadraoISO = (codigoOperacao as 'BRL' | 'PYG' | 'USD' | 'ARS');

        // Recalcular matriz de moedas visiveis no bloco "Recebido em"
        this.atualizarMoedasVisiveis();

        // Carregar formato de impressao padrao da empresa
        const tipoImpressaoEmpresa = (res as any)?.tipoDeFormatoDaImpressao;
        if (tipoImpressaoEmpresa) {
          // O campo pode vir como string direta ou como objeto com propriedade 'enum'
          const valorImpressao = typeof tipoImpressaoEmpresa === 'string'
            ? tipoImpressaoEmpresa
            : tipoImpressaoEmpresa?.enum;
          if (valorImpressao) {
            this.tipoDeImpressaoForm.patchValue({ impressao: valorImpressao });
          }
        }

        // Carrega opcao de impressao automatica da empresa
        const rawTipoImprimir = (res as any)?.tipoImprimir;
        this.tipoImprimir = typeof rawTipoImprimir === 'string'
          ? rawTipoImprimir
          : rawTipoImprimir?.tipoEnum ?? rawTipoImprimir?.enum ?? 'PERGUNTAR';

        // Flag "Imprimir ficha do evento" da empresa (controla botao Imprimir Ficha)
        this.deveImprimirFicha = (res as any)?.deveImprimirFicha === true;

        // Carregar cotacoes apos moeda estar definida
        this.carregarCotacoes();
      },
      error: () => {
        this.moedaPadraoISO = 'PYG';
        this.deveImprimirFicha = false;
        this.atualizarMoedasVisiveis();
        this.carregarCotacoes();
      },
    });
  }

  /**
   * Converte codigo ISO (BRL/PYG/USD/ARS) para o enum Moeda correspondente.
   * Retorna undefined para valores desconhecidos/indefinidos.
   * Usado para alimentar moedasCompativeis() a partir de moedaPadraoISO.
   */
  private isoToMoeda(iso: 'BRL' | 'PYG' | 'USD' | 'ARS' | string | undefined): Moeda | undefined {
    switch (iso) {
      case 'BRL': return Moeda.REAL;
      case 'PYG': return Moeda.GUARANI;
      case 'USD': return Moeda.DOLAR;
      case 'ARS': return Moeda.PESO;
      default:    return undefined;
    }
  }

  /**
   * Moeda da nota de devolucao (= moeda padrao da empresa) no formato enum,
   * para alimentar o `[moedaNota]` do forma-pagamento-dialog — que filtra o
   * dropdown de contas, ocultando moedas incompativeis.
   */
  get moedaNotaEnum(): Moeda | undefined {
    return this.isoToMoeda(this.moedaPadraoISO);
  }

  /**
   * Recalcula moedasVisiveis a partir de moedaPadraoISO, aplicando a matriz
   * de moedas compativeis. Fallback Set([GUARANI]) quando ISO e desconhecido
   * ou indefinido (consistente com forma-pagamento-dialog).
   */
  private atualizarMoedasVisiveis(): void {
    const moedaPadraoEnum = this.isoToMoeda(this.moedaPadraoISO);
    if (moedaPadraoEnum) {
      this.moedasVisiveis = new Set(moedasCompativeis(moedaPadraoEnum));
    } else {
      this.moedasVisiveis = new Set([Moeda.GUARANI]);
    }
  }

  /**
   * Retorna a chave i18n do badge de metodo de pagamento.
   * Tres categorias mutuamente exclusivas, com prioridade:
   *  1. 'fatura.labels.credito' → venda parcelada (ha titulos a receber).
   *  2. 'fatura.labels.em_conta' → pagamento via conta bancaria/movimentacao.
   *  3. 'fatura.labels.contado' → pagamento a vista em dinheiro/efetivo.
   */
  /**
   * Situacao da parcela normalizada ('PAGO' | 'ABERTO' | 'CANCELADO').
   *
   * O backend serializa SituacaoDeCobranca como LocalizableEnum ({ enum, nome, ... }),
   * mas em alguns fluxos o valor chega como string direta — tratamos os dois casos.
   */
  situacaoDoTitulo(titulo: TituloDTO): string {
    const situacao: any = titulo?.situacaoDeCobranca;
    if (!situacao) return 'ABERTO';
    if (typeof situacao === 'object' && situacao?.enum) return String(situacao.enum).toUpperCase();
    return String(situacao).toUpperCase();
  }

  tituloPago(titulo: TituloDTO): boolean {
    return this.situacaoDoTitulo(titulo) === 'PAGO';
  }

  tituloCancelado(titulo: TituloDTO): boolean {
    return this.situacaoDoTitulo(titulo) === 'CANCELADO';
  }

  /** Chave i18n do badge de situacao da parcela. */
  situacaoTituloBadgeKey(titulo: TituloDTO): string {
    if (this.tituloPago(titulo)) return 'fatura.labels.titulo_pago';
    if (this.tituloCancelado(titulo)) return 'fatura.labels.titulo_cancelado';
    return 'fatura.labels.titulo_aberto';
  }

  /**
   * Pagamentos ja registrados na parcela, do mais antigo para o mais recente.
   *
   * Uma parcela pode ser quitada em varios pagamentos parciais — cada `tipoDeCobranca`
   * e um movimento que abateu parte do titulo. Mesmo criterio da tela de contas a receber.
   */
  pagamentosDoTitulo(titulo: TituloDTO): TipoDeCobrancaDTO[] {
    return [...(titulo?.tipoDeCobranca ?? [])].sort((a, b) => {
      const dataA = a.emissaoMovimento ? new Date(a.emissaoMovimento).getTime() : 0;
      const dataB = b.emissaoMovimento ? new Date(b.emissaoMovimento).getTime() : 0;
      return dataA - dataB;
    });
  }

  /** Valor de um pagamento: o valor do movimento quando existe, senao o do proprio vinculo. */
  valorDoPagamento(pagamento: TipoDeCobrancaDTO): number {
    return pagamento.valorMovimento ?? pagamento.valor ?? 0;
  }

  /** Soma dos pagamentos ja registrados na parcela. */
  totalPagoTitulo(titulo: TituloDTO): number {
    return (titulo?.tipoDeCobranca ?? []).reduce((total, p) => total + this.valorDoPagamento(p), 0);
  }

  /**
   * Os titulos desta nota vao para contas a receber (venda) ou contas a pagar (compra)?
   *
   * A resposta vem do proprio titulo (`tipoOperacaoFinanceira`); so quando o backend nao
   * mandou o campo caimos no tipo da transacao, que e o criterio usado para gera-lo.
   */
  get titulosSaoAReceber(): boolean {
    const operacao: any = this.form.titulos?.[0]?.tipoOperacaoFinanceira;
    const valor = (typeof operacao === 'object' && operacao?.enum) ? operacao.enum : operacao;
    if (valor) return String(valor).toUpperCase() === 'ENTRADA';
    return !(this.ehTipoDeTransacaoCompra || this.ehTipoDeTransacaoDevolucaoDeCompra);
  }

  /**
   * Libera a navegacao da parcela para a tela de titulos da pessoa.
   *
   * Sem pessoa ou sem titulo nao ha para onde ir; sem a permissao do modulo o guard da rota
   * barraria de qualquer forma — melhor nem oferecer o clique.
   */
  get podeAbrirTitulosDaPessoa(): boolean {
    if (!this.form.pessoa_id || (this.form.titulos?.length ?? 0) === 0) return false;
    return this.permissaoService.temPermissao(this.titulosSaoAReceber ? 'receitas' : 'gastos');
  }

  /** Abre a tela de titulos da pessoa, o mesmo destino do clique numa conta no dashboard. */
  abrirTitulosDaPessoa(): void {
    if (!this.podeAbrirTitulosDaPessoa) return;

    const rota = this.titulosSaoAReceber ? '/contas-a-receber/pessoa' : '/contas-a-pagar/pessoa';
    const queryParams: Record<string, string> = { nome: this.form.nomePessoa ?? '' };
    if (!this.titulosSaoAReceber) {
      queryParams['tipo'] = 'SAIDA';
    }
    this.router.navigate([rota, this.form.pessoa_id], { queryParams });
  }

  get metodoPagamentoBadgeKey(): string {
    if ((this.form.numeroParcelas ?? 0) > 0) {
      return 'fatura.labels.credito';
    }
    if ((this.form.valorRecebidoNaConta ?? 0) > 0) {
      return 'fatura.labels.em_conta';
    }
    return 'fatura.labels.contado';
  }

  /** Carrega a cotacao mais recente para preencher cotPYG/cotBRL/cotPesoGuarani.
   *  Usa /cotacao/ultimaCotacao (mesmo padrao do PDV e do app mobile) ao inves
   *  de /cotacao/cotacaoAtual, que filtra por LocalDate.now() e pode retornar
   *  404 quando nao ha cotacao do dia, fazendo o card Peso sumir do dialog
   *  de devolucao para empresas com moeda padrao PESO. */
  carregarCotacoes(): void {
    this.cotacaoService.get('/cotacao/ultimaCotacao').subscribe({
      next: (res: any) => {
        if (res) {
          this.cotPYG = res.dolarGuarani || 7180;
          this.cotBRL = res.dolarReal || 5.47;
          // pesoGuarani e opcional; sem valor, coluna peso permanece oculta
          this.cotPesoGuarani = res.pesoGuarani ?? 0;
          // realGuarani e a cotacao direta BRL<->PYG; sem ela a util triangula via USD
          // e introduz divergencia de spread entre os pares USD/BRL e USD/PYG.
          this.cotRealGuarani = res.realGuarani ?? 0;
        }
      },
      error: () => {
        // Manter valores padrao
      },
    });
  }

  /**
   * Copia variantes e modificadores do item da venda para o item da devolucao, sem `id` (o backend
   * cria registros novos). Quantidade e total seguem a quantidade devolvida: a variante vale uma
   * por unidade e o modificador mantem a proporcao por unidade da venda original.
   */
  private personalizacaoParaDevolucao(itemOriginal: any, quantidadeDevolvida: number): { variantesDePreco?: any[]; modificadores?: any[] } {
    const resultado: { variantesDePreco?: any[]; modificadores?: any[] } = {};
    const quantidadeOriginal = Number(itemOriginal?.quantidade) || 0;
    const variantes: any[] = itemOriginal?.variantesDePreco || [];
    const modificadores: any[] = itemOriginal?.modificadores || [];

    if (variantes.length) {
      resultado.variantesDePreco = variantes.map((v) => ({
        codigoDaVariante: v.codigoDaVariante,
        descricao: v.descricao,
        quantidade: quantidadeDevolvida,
        precoDeVenda: v.precoDeVenda,
        precoTotal: (Number(v.precoDeVenda) || 0) * quantidadeDevolvida,
      }));
    }
    if (modificadores.length) {
      resultado.modificadores = modificadores.map((m) => {
        const porUnidade = quantidadeOriginal > 0 ? (Number(m.quantidade) || 0) / quantidadeOriginal : 1;
        const quantidade = Math.max(1, Math.round(porUnidade * quantidadeDevolvida));
        return {
          codigoDoModificador: m.codigoDoModificador,
          descricao: m.descricao,
          quantidade,
          precoDeVenda: m.precoDeVenda,
          precoTotal: (Number(m.precoDeVenda) || 0) * quantidade,
        };
      });
    }
    return resultado;
  }

  onDevolucaoConfirmada(result: FormaPagamentoResult): void {
    if (this.processandoDevolucao) return;
    this.processandoDevolucao = true;

    // Determinar tipo de transacao da devolucao
    const tipoTransacaoDevolucao = this.ehTipoDeTransacaoVenda
      ? TipoTransacao.DEVOLUCAO
      : TipoTransacao.DEVOLUCAO_DE_COMPRA;

    // Montar itens de devolucao (apenas os com quantidade > 0)
    const itensParaDevolver = this.itensDeNotaDevolucao
      .filter(item => item.quantidade > 0)
      .map(item => {
        const itemDeNotaOriginal: any = this.form.itemDeNota.find(i => i.item_id === item.id);
        const valorUnitarioComDesconto = this.calcularValorUnitarioItemDevolucaoComDesconto(item);
        const valorTotalDevolucao = valorUnitarioComDesconto * item.quantidade;
        // tipoDeImposto vem da API como objeto {enum, nome, id} — extrair apenas o enum string
        const tipoDeImpostoRaw = itemDeNotaOriginal?.tipoDeImposto;
        const tipoDeImposto = typeof tipoDeImpostoRaw === 'object' && tipoDeImpostoRaw?.enum
          ? tipoDeImpostoRaw.enum
          : (tipoDeImpostoRaw || 'IVA_10');

        return {
          item_id: item.id,
          quantidade: item.quantidade,
          valorUnitario: valorUnitarioComDesconto,
          valorTotal: valorTotalDevolucao,
          desconto: 0,
          valorUnitarioComDesconto: valorUnitarioComDesconto,
          valorTotalComDesconto: valorTotalDevolucao,
          tipoDeImposto: tipoDeImposto,
          custoUnitario: itemDeNotaOriginal?.custoUnitario || 0,
          custoTotal: (itemDeNotaOriginal?.custoUnitario || 0) * item.quantidade,
          observacao: itemDeNotaOriginal?.observacao || item.nome || '',
          // Personalizacao da venda (variantes/modificadores), proporcional a quantidade devolvida,
          // para a nota de devolucao imprimir o que foi devolvido. Paridade com o mobile.
          ...this.personalizacaoParaDevolucao(itemDeNotaOriginal, item.quantidade),
        };
      });

    const totalDevolucao = this.calcularValorTotalItensDeNotaDevolucao();

    // Determinar se a devolucao sera fatura legal (quando venda original eh fatura legal e timbrado selecionado)
    const devolucaoEhFaturaLegal = !!(this.ehTipoDeTransacaoVenda && this.selectedTimbrado);

    // Montar payload da nota de devolucao
    // Nota: nao enviar 'emissao' — backend usa LocalDateTime.now() como default
    const notaDevolucao: any = {
      tipoDeTransacao: tipoTransacaoDevolucao,
      ehFaturaLegal: devolucaoEhFaturaLegal,
      tipoNota: devolucaoEhFaturaLegal ? this.selectedTimbrado!.tipoNota : (this.form.tipoNota || 'MANUAL'),
      timbrado_id: this.selectedTimbrado?.id || null,
      numeroTimbrado: this.selectedTimbrado?.timbrado || null,
      numeroFatura: this.selectedTimbrado?.proximoNumero || null,
      numeroPontoExpedicao: this.selectedTimbrado?.pontoDeExpedicao || null,
      numeroFilial: this.selectedTimbrado?.numeroFilial || null,
      custoTotalDosItens: 0,
      tipoDeDesconto: 'SEM_DESCONTO',
      porcentagemDesconto: 0,
      valorTotal: totalDevolucao,
      valorTotalComDesconto: totalDevolucao,
      valorDoDesconto: 0,
      moeda: this.form.moeda,
      cancelado: false,
      notaDevolvida_id: this.form.id,
      pessoa_id: this.form.pessoa_id,
      vendedor_id: this.form.vendedor_id || null,
      cotacao_id: this.form.cotacao_id || null,
      informacaoDaTransacao: this.form.informacaoDaTransacao || '',
      tipoContaEstoque: 'NORMAL',
      itemDeNota: itensParaDevolver,
      baixas: [],
      titulos: [],
    };

    // Preencher dados de pagamento conforme forma selecionada
    if (result.forma === 'dinheiro') {
      const dados = result.dados as DadosPagamentoDinheiro;
      notaDevolucao.condicaoDePagamento = 'CONTADO';
      notaDevolucao.valorRecebidoEmGuarani = dados.valorRecebidoEmGuarani || 0;
      notaDevolucao.valorRecebidoEmDolar = dados.valorRecebidoEmDolar || 0;
      notaDevolucao.valorRecebidoEmReal = dados.valorRecebidoEmReal || 0;
      notaDevolucao.valorRecebidoEmPeso = dados.valorRecebidoEmPeso || 0;
      notaDevolucao.valorRecebidoNaConta = 0;
    }

    if (result.forma === 'conta') {
      const dados = result.dados as DadosPagamentoConta;
      notaDevolucao.condicaoDePagamento = 'CONTADO';
      // Backend grava Baixa.valor = valorRecebidoNaConta direto (sem reconverter).
      // Origem da conversao = moeda padrao da empresa (totalDevolucao esta nessa
      // moeda). Converte direto par-a-par quando a moeda da conta diferir.
      //
      // Guard de pareamento: a conversao direta proibe ARS<->USD/BRL (espelha o
      // backend). Valida ANTES do POST e aborta com toast caso a conta escolhida
      // seja incompativel — nao usa excecao como fluxo. Como este metodo nao tem
      // try/catch envolvente, processandoDevolucao precisa ser resetado aqui.
      const moedaEmpresaEnum = this.isoToMoeda(this.moedaPadraoISO) || Moeda.GUARANI;
      if (dados.moedaDaConta && !podeConverterEntre(moedaEmpresaEnum, dados.moedaDaConta)) {
        this.messageService.add({
          severity: 'warn',
          summary: this.translate.instant('forma_pagamento.moeda_incompativel_titulo'),
          detail: this.translate.instant('forma_pagamento.moeda_incompativel_detalhe'),
        });
        this.processandoDevolucao = false;
        return;
      }
      // Defesa em profundidade: a excecao de par proibido nunca deve quebrar a tela.
      try {
        notaDevolucao.valorRecebidoNaConta = this.calcularValorRecebidoNaConta(
          totalDevolucao,
          dados.moedaDaConta,
        );
      } catch (erroConversao) {
        if (erroConversao instanceof ConversaoNaoPermitidaError) {
          this.messageService.add({
            severity: 'warn',
            summary: this.translate.instant('forma_pagamento.moeda_incompativel_titulo'),
            detail: this.translate.instant('forma_pagamento.moeda_incompativel_detalhe'),
          });
          this.processandoDevolucao = false;
          return;
        }
        throw erroConversao;
      }
      notaDevolucao.conta_id = dados.conta_id;
      notaDevolucao.moedaDaConta = dados.moedaDaConta;
      notaDevolucao.informacaoDaTransacao = dados.informacaoDaTransacao;
      notaDevolucao.valorRecebidoEmGuarani = 0;
      notaDevolucao.valorRecebidoEmDolar = 0;
      notaDevolucao.valorRecebidoEmReal = 0;
      notaDevolucao.valorRecebidoEmPeso = 0;
    }

    // Tipo da nota de devolucao para verificar se precisa transmitir
    const tipoNotaDevolucao = notaDevolucao.tipoNota;

    // Enviar ao backend
    this.notaService.post('/nota', notaDevolucao).pipe(
      finalize(() => { this.processandoDevolucao = false; })
    ).subscribe({
      next: (respostaNota: any) => {
        this.mostrarDialogPagamentoDevolucao = false;

        // Se a devolucao eh fatura legal eletronica, transmitir automaticamente
        const ehEletronica = tipoNotaDevolucao === TipoPadraoNota.ELETRONICA;
        if (devolucaoEhFaturaLegal && ehEletronica && respostaNota?.id) {
          this.transmitirNfeDevolucaoAutomatica(respostaNota.id);
        } else {
          this.showSuccess(this.translate.instant('fatura.messages.devolucao_realizada'));
          // Carregar a nota de devolucao recem-criada (nao a nota original)
          if (respostaNota?.id) {
            // Registrar acao pendente de impressao para ser executada apos loadNota
            this.pendingPrintAction = this.tipoImprimir;
            this.loadNota(respostaNota.id);
          }
        }
      },
      error: (err: any) => {
        const mensagem = err?.error?.message || this.translate.instant('fatura.messages.erro_devolucao');
        this.showError(mensagem);
      },
    });
  }

  private async loadPdfNota(): Promise<void> {
    const tipoImpressao = this.tipoDeImpressaoForm.value?.impressao;
    if (!tipoImpressao) {
      this.showError(this.translate.instant('fatura-view.mensagens.selecione_tipo_impressao'));
      return;
    }

    try {
      const response = await this.notaService.gerarPdfNota(`/nota/gerar-pdf-nota/${this.notaId}/impressao/${tipoImpressao}`).toPromise();
      this.pdf = response || '';
    } catch (error) {
      this.showError(this.translate.instant('fatura-view.mensagens.erro_carregar_fatura'));
    }
  }

  private imprimirAutomaticoViaDevolucao(): void {
    if (!this.notaId) return;

    const formato = this.tipoDeImpressaoForm?.value?.impressao || 'BOBINA_58';

    this.notaService.gerarPdfNota(`/nota/gerar-pdf-nota/${this.notaId}/impressao/${formato}`).subscribe({
      next: (pdfBase64: string) => {
        if (!pdfBase64) return;

        const byteCharacters = atob(pdfBase64);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: 'application/pdf' });
        const url = URL.createObjectURL(blob);

        let iframe = document.getElementById('avati-iframe-impressao') as HTMLIFrameElement;
        if (!iframe) {
          iframe = document.createElement('iframe');
          iframe.id = 'avati-iframe-impressao';
          iframe.style.display = 'none';
          document.body.appendChild(iframe);
        }
        iframe.src = url;
        iframe.onload = () => {
          try { iframe.contentWindow?.print(); }
          catch (e) { window.open(url, '_blank'); }
        };
      },
      error: () => {
        this.showError(this.translate.instant('fatura-view.mensagens.erro_gerar_pdf'));
      }
    });
  }

  async imprimir() {
    const tipoImpressao = this.tipoDeImpressaoForm.value?.impressao;
    if (!tipoImpressao) {
      this.showError(this.translate.instant('fatura-view.mensagens.selecione_tipo_impressao'));
      return;
    }

    this.isImprimindoFatura = true;
    try {
      await this.loadPdfNota();

      if (this.pdf) {
        this.openPdfFromBase64(this.pdf);
      } else {
        this.showError(this.translate.instant('fatura-view.mensagens.erro_carregar_fatura'));
      }
    } finally {
      this.isImprimindoFatura = false;
    }
  }

  /**
   * Imprime a ficha do evento da venda. O backend decide o template pela
   * configuracao da empresa (Tipo de Ficha do Evento), por isso enviamos
   * apenas o notaId (sem selecao de formato). Espelha o mobile fatura_screen.
   */
  async imprimirFicha(): Promise<void> {
    if (!this.notaId) {
      this.showError(this.translate.instant('fatura-view.mensagens.erro_imprimir_ficha'));
      return;
    }

    this.isImprimindoFicha = true;
    try {
      const pdfBase64 = await firstValueFrom(this.notaService.gerarPdfFicha(this.notaId));

      if (pdfBase64) {
        const janela = window.open(this.base64ParaUrlPdf(pdfBase64));
        if (!janela) {
          this.showError(this.translate.instant('fatura-view.mensagens.popup_bloqueado_ficha'));
        }
      } else {
        this.showError(this.translate.instant('fatura-view.mensagens.erro_imprimir_ficha'));
      }
    } catch (error) {
      this.showError(this.translate.instant('fatura-view.mensagens.erro_imprimir_ficha'));
    } finally {
      this.isImprimindoFicha = false;
    }
  }

  /** Converte um PDF em base64 numa object URL (blob) para abrir em nova aba. */
  private base64ParaUrlPdf(base64: string): string {
    const byteCharacters = atob(base64);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    const blob = new Blob([byteArray], { type: 'application/pdf' });
    return URL.createObjectURL(blob);
  }

  async downloadPDF() {
    this.isImprimindoFatura = true;
    try {
      await this.loadPdfNota();

      if (!this.pdf) {
        this.showError(this.translate.instant('fatura-view.mensagens.nenhum_pdf_disponivel'));
        return;
      }

      const byteCharacters = atob(this.pdf);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);

      const blob = new Blob([byteArray], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `nota-${this.notaId}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error(error);
      this.showError(this.translate.instant('fatura-view.mensagens.erro_download_pdf'));
    } finally {
      this.isImprimindoFatura = false;
    }
  }

  // Helper para criar delay
  private delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

  abrirDialogoConfirmarTransmissao(): void {
    if (this.notaCancelada) {
      this.avisarNotaCancelada();
      return;
    }
    this.dialogoConfirmarTransmissaoVisivel = true;
  }

  confirmarTransmissao(): void {
    this.dialogoConfirmarTransmissaoVisivel = false;
    this.transmitirNfe();
  }

  async transmitirNfe() {
    if (this.isTransmitindo) return;
    this.isTransmitindo = true;

    this.abrirDialogoOperacaoNfe('transmissao');

    this.operacaoNfeProgresso = 25;
    await this.delay(this.PROGRESS_BAR_STEP_DELAY);
    this.operacaoNfeProgresso = 50;

    try {
      const response: any = await firstValueFrom(
        this.nfeService.post(`/nfe/transmitir/${this.notaId}`, undefined)
      );

      console.log('Resposta da transmissão NFE:', response);

      this.operacaoNfeProgresso = 75;

      // Atualizar estado baseado na resposta do servidor
      if (response?.descMensagem) {
        this.atualizarEstadoUI(response.descMensagem, response);
      } else {
        // Fallback: consultar para obter estado real (sem controlar progresso aqui)
        await this.consultarNfe();
      }

      this.finalizarOperacaoNfeSucesso();
    } catch (error: any) {
      if (error?.error?.errors?.length > 0 && error.error.errors[0].grupo) {
        // Erros com campo grupo → agrupar e exibir com botoes de acao
        this.gruposErroNfe = this.agruparErrosNfe(error.error.errors);
        this.temErrosAgrupados = true;
        this.finalizarOperacaoNfeErro('');
      } else if (error?.error?.errors?.length > 0) {
        // Erros sem grupo → exibir como lista simples (fallback)
        const mensagemErro = error.error.errors.map((e: any) => e.mensagem).join('\n');
        this.temErrosAgrupados = false;
        this.finalizarOperacaoNfeErro(mensagemErro);
      } else {
        // Erro generico
        const mensagemErro = error?.error?.message || error?.message ||
          this.translate.instant('fatura-view.mensagens.erro_transmitir_nfe');
        this.temErrosAgrupados = false;
        this.finalizarOperacaoNfeErro(mensagemErro);
      }

      console.error('Erro na transmissão NFE:', error);
    } finally {
      this.isTransmitindo = false;
    }
  }



  private async transmitirNfeDevolucaoAutomatica(notaDevolucaoId: number): Promise<void> {
    this.abrirDialogoOperacaoNfe('transmissao');

    this.operacaoNfeProgresso = 25;
    await this.delay(this.PROGRESS_BAR_STEP_DELAY);
    this.operacaoNfeProgresso = 50;

    try {
      const response: any = await firstValueFrom(
        this.nfeService.post(`/nfe/transmitir/${notaDevolucaoId}`, undefined)
      );

      this.operacaoNfeProgresso = 75;

      // Apos transmissao bem-sucedida, exibir fatura da devolucao (nao da nota original)
      this.operacaoNfeEstado = 'sucesso';
      this.operacaoNfeProgresso = 100;
      setTimeout(() => {
        this.dialogoOperacaoNfeVisivel = false;
        this.loadNota(notaDevolucaoId);
      }, 2000);
    } catch (error: any) {
      console.warn('Erro na transmissao NFe da devolucao (suprimido para o usuario):', error);

      // Recarregar a nota de devolucao mesmo em caso de erro na transmissao
      this.dialogoOperacaoNfeVisivel = false;
      this.loadNota(notaDevolucaoId);
    }
  }

  async buscarEventoEnvioNfe() {
    try {
      console.log('Buscando evento de envio para notaId:', this.notaId);

      if (this.notaId) {
        // Exibe um loading ou inicia o progresso se desejar
        const response: any = await firstValueFrom(
          this.nfeService.getById(`/nfe/buscar-evento-envio-por-id-nfe`, this.notaId.toString())
        );

        // Conforme o console, a resposta é um Array(56)
        this.historicoEventosEnvioNfe = Array.isArray(response) ? response : [response];

        console.log('Eventos carregados:', this.historicoEventosEnvioNfe.length);

        // Abre o diálogo após garantir que os dados existem
        this.dialogoHistoricoEventosVisivel = true;
      }
    } catch (error) {
      console.error('Erro ao buscar histórico:', error);
      // Exibir toast de erro do PrimeNG aqui
    }
  }

  /**
   * Abre o dialogo de motivo, registrando qual cancelamento sera executado na confirmacao.
   * O dialogo e o mesmo para os dois casos; muda o titulo e a acao do botao confirmar.
   */
  async abrirDialogoCancelamento(tipo: 'nfe' | 'nota'): Promise<void> {
    this.tipoCancelamentoSolicitado = tipo;
    this.motivoCancelamentoNfe = '';
    this.resumoCancelamento = null;
    this.estornoDePagamentosAceito = false;
    this.dialogoMotivoCancelamentoVisivel = true;

    await this.carregarResumoCancelamento();
  }

  /**
   * Busca o que sera estornado para o dialogo poder avisar antes da confirmacao.
   *
   * So consulta quando ha pagamento a estornar. O caso comum — nota so com parcelas ABERTAS — e
   * decidido aqui mesmo (`saldo` e `valor` ja vieram com a nota) e cancela direto, sem ida ao
   * servidor e sem confirmacao nenhuma.
   *
   * Falha de consulta nao trava o cancelamento: o dialogo segue como o simples e, se houver
   * pagamento mesmo, o backend recusa (sem o aceite) — ninguem estorna nada por engano.
   */
  private async carregarResumoCancelamento(): Promise<void> {
    const notaId = this.notaId;
    if (!notaId) return;
    if (!this.notaPossuiPagamento) return;

    this.carregandoResumoCancelamento = true;
    try {
      this.resumoCancelamento = await firstValueFrom(
        this.notaService.resumoDeCancelamento(notaId)
      );
    } catch {
      this.resumoCancelamento = null;
    } finally {
      this.carregandoResumoCancelamento = false;
    }
  }

  /**
   * Ha parcela paga (total ou parcialmente) nesta nota?
   *
   * Mesma regra do backend (`Titulo.possuiPagamento()`: `saldo < valor`), avaliada nos titulos que
   * a tela ja carregou. Parcela ABERTA tem `saldo == valor` e nao entra aqui.
   */
  get notaPossuiPagamento(): boolean {
    return (this.form?.titulos ?? []).some((titulo: any) => titulo.saldo < titulo.valor);
  }

  /** O dialogo precisa avisar do estorno? So quando existe pagamento recebido nesta nota. */
  get exigeConfirmacaoDeEstorno(): boolean {
    return this.resumoCancelamento?.possuiPagamento === true;
  }

  /**
   * Libera o botao de confirmar: motivo preenchido e, havendo pagamento, o aceite do estorno.
   * Sem o aceite o backend recusaria de qualquer forma — aqui o usuario entende o porque antes.
   */
  get podeConfirmarCancelamento(): boolean {
    const motivoValido = !!this.motivoCancelamentoNfe && this.motivoCancelamentoNfe.length >= 5;
    if (!motivoValido) return false;
    if (this.carregandoResumoCancelamento) return false;
    return !this.exigeConfirmacaoDeEstorno || this.estornoDePagamentosAceito;
  }

  /** Executa o cancelamento correspondente ao que abriu o dialogo. */
  async confirmarCancelamento(motivo: string): Promise<void> {
    // So propaga o aceite quando ha mesmo o que estornar: mandar `true` numa nota sem pagamento
    // seria ruido no contrato, e o backend ignoraria.
    const confirmaEstorno = this.exigeConfirmacaoDeEstorno && this.estornoDePagamentosAceito;
    if (this.tipoCancelamentoSolicitado === 'nota') {
      await this.cancelarNota(motivo, confirmaEstorno);
      return;
    }
    await this.cancelarNfe(motivo, confirmaEstorno);
  }

  /**
   * Indica se a nota pode ser cancelada apenas no sistema (endpoint `/nota/cancelar`).
   *
   * Vale para nota que NUNCA teve NF-e aprovada. Quando existe NF-e valida no fisco
   * (AUTORIZADA, APROVADO ou ENVIADA), o cancelamento tem de passar pelo fluxo fiscal
   * (`/nfe/cancelar`) — cancelar so no sistema deixaria o Avati divergente do fisco.
   * Por isso este metodo e falso nesses estados INCLUSIVE depois das 72h: passado o prazo,
   * o caminho e devolucao/nota de credito, nao um cancelamento interno silencioso.
   */
  /**
   * Nota cancelada e somente leitura.
   *
   * Cancelamento desfaz itens, estoque e baixas no backend: qualquer acao que altere a nota
   * depois disso (virar fatura legal, trocar timbrado, devolver, transmitir ao fisco) deixaria
   * o Avati inconsistente. Consultar e imprimir continuam liberados — o documento ainda existe
   * para conferencia.
   */
  get notaCancelada(): boolean {
    return this.form?.cancelado === true;
  }

  /** Aviso padrao quando o usuario tenta disparar uma acao numa nota cancelada. */
  private avisarNotaCancelada(): void {
    this.showError(this.translate.instant('fatura-view.mensagens.acao_nao_permitida_nota_cancelada'));
  }

  /** Regras normais (permissao + estado da nota) para o cancelamento simples, sem considerar contexto de contador. */
  private elegivelParaCancelamentoNotaSimples(): boolean {
    if (!this.podeCancelarNotaPermissao) return false;
    if (this.form?.cancelado) return false;

    const estadosComNfeValidaNoFisco = [
      EstadoNotaEletronica.AUTORIZADA,
      EstadoNotaEletronica.APROVADO,
      EstadoNotaEletronica.ENVIADA
    ];

    return !estadosComNfeValidaNoFisco.includes(this.estadoNotaEletronica.value);
  }

  /**
   * HOTFIX (contexto de contador): `PUT /nota/cancelar/{id}` nao esta na allowlist de
   * contexto do backend (so GET /nota/ e honrado) — com contexto de contador ativo, a
   * chamada executa na empresa do CONTADOR, nao na do cliente, cancelando em cascata a
   * nota ERRADA (itens, estoque e baixas). Ate essa rota ganhar allowlist no backend (fora
   * de escopo: operacao destrutiva nao entra na allowlist), o cancelamento simples fica
   * bloqueado neste contexto. O caminho seguro do contador para desfazer uma nota
   * permanece o fiscal (`abrirDialogoCancelamento('nfe')` / `cancelarNfe`), que nao e
   * afetado por este bloqueio.
   */
  get contextoContadorAtivo(): boolean {
    return this.contadorContextoService.clienteAtual() !== null;
  }

  podeCancelarNotaSimples(): boolean {
    if (this.contextoContadorAtivo) return false;
    return this.elegivelParaCancelamentoNotaSimples();
  }

  /**
   * True quando o cancelamento simples SERIA oferecido (permissao/estado ok), mas esta
   * bloqueado por causa do contexto de contador ativo. Usado para exibir a mensagem
   * explicativa no lugar do botao (ver `contextoContadorAtivo`).
   */
  get cancelamentoNotaBloqueadoPorContexto(): boolean {
    return this.contextoContadorAtivo && this.elegivelParaCancelamentoNotaSimples();
  }

  /**
   * Cancela a nota apenas no sistema, sem emitir nada ao fisco.
   * Cancela em cascata os itens, o estoque e as baixas financeiras da nota (regra do backend).
   */
  async cancelarNota(motivoDeCancelamento: string, confirmaEstornoDePagamentos = false): Promise<void> {
    if (this.isCancelando) return;

    // Guarda de seguranca redundante ao `podeCancelarNotaSimples()` do template: garante que
    // a chamada destrutiva nunca dispara sob contexto de contador, mesmo se este metodo for
    // acionado por outra via (ex.: atalho de teclado, chamada direta).
    if (this.contextoContadorAtivo) {
      this.dialogoMotivoCancelamentoVisivel = false;
      this.messageService.add({
        severity: 'warn',
        summary: this.translate.instant('fatura-view.mensagens.cancelamento_nota_bloqueado_contexto_contador')
      });
      return;
    }

    const notaId = this.notaId;
    if (!notaId) return;

    this.isCancelando = true;
    this.dialogoMotivoCancelamentoVisivel = false;

    try {
      await firstValueFrom(
        this.notaService.cancelarNota(notaId, motivoDeCancelamento, confirmaEstornoDePagamentos)
      );

      this.form.cancelado = true;
      this.messageService.add({
        severity: 'success',
        summary: this.translate.instant('fatura-view.mensagens.nota_cancelada')
      });

      // Recarrega para refletir estado, itens e baixas ja cancelados pelo backend.
      this.loadNota(notaId);
    } catch (error: any) {
      const mensagemErro = error?.error?.message
        || error?.message
        || this.translate.instant('fatura-view.mensagens.erro_cancelar_nota');
      this.messageService.add({ severity: 'error', summary: mensagemErro });
    } finally {
      this.isCancelando = false;
    }
  }

  async cancelarNfe(motivoDeCancelamento: string, confirmaEstornoDePagamentos = false) {
    if (this.isCancelando) return;
    this.isCancelando = true;

    this.dialogoMotivoCancelamentoVisivel = false;
    this.abrirDialogoOperacaoNfe('cancelamento');

    this.operacaoNfeProgresso = 25;
    await this.delay(this.PROGRESS_BAR_STEP_DELAY);
    this.operacaoNfeProgresso = 50;

    try {
      console.log('Cancelando NFE para notaId:', this.notaId, 'com motivo:', motivoDeCancelamento);

      const response: any = await firstValueFrom(
        this.nfeService.patch(`/nfe/cancelar/${this.notaId}?motivoDeCancelamento=${encodeURIComponent(motivoDeCancelamento)}&confirmaEstornoDePagamentos=${confirmaEstornoDePagamentos}`, undefined)
      );

      console.log('Resposta do cancelamento:', response);

      this.estadoNotaEletronica = {
        value: EstadoNotaEletronica.CANCELADA,
        label: this.translate.instant(
          `enums.estado_nota_eletronica.${EstadoNotaEletronica.CANCELADA.toLowerCase()}`
        )
      };

      await this.consultarNfe();
      this.finalizarOperacaoNfeSucesso();
    } catch (error: any) {
      const mensagemErro = error?.error?.message || error?.message || this.translate.instant('fatura-view.mensagens.erro_cancelar_nfe');
      this.finalizarOperacaoNfeErro(mensagemErro);
      console.error('Erro no cancelamento NFE:', error);
    } finally {
      this.isCancelando = false;
    }
  }

  async inutilizarNfe(motivoDeInutilizacao: string) {
    if (this.isInutilizando) return;
    if (this.notaCancelada) {
      this.avisarNotaCancelada();
      return;
    }
    this.isInutilizando = true;

    this.dialogoMotivoInutilizacaoVisivel = false;
    this.abrirDialogoOperacaoNfe('inutilizacao');

    this.operacaoNfeProgresso = 25;
    await this.delay(this.PROGRESS_BAR_STEP_DELAY);
    this.operacaoNfeProgresso = 50;

    try {
      const response: any = await firstValueFrom(
        this.nfeService.patch(`/nfe/inutilizar/${this.notaId}/${motivoDeInutilizacao}`, undefined)
      );
      console.log('Resposta da inutilização:', response);

      this.operacaoNfeProgresso = 75;

      this.estadoNotaEletronica = {
        value: EstadoNotaEletronica.INUTILIZADA,
        label: this.translate.instant(
          `enums.estado_nota_eletronica.${EstadoNotaEletronica.INUTILIZADA.toLowerCase()}`
        )
      };

      await this.consultarNfe();
      this.finalizarOperacaoNfeSucesso();
    } catch (error: any) {
      const mensagemErro = error?.error?.message || error?.message || this.translate.instant('fatura-view.mensagens.erro_inutilizar_nfe');
      this.finalizarOperacaoNfeErro(mensagemErro);
      console.error('Erro na inutilização NFE:', error);
    } finally {
      this.isInutilizando = false;
    }
  }

  private atualizarEstadoUI(statusSief: string, dadosNfe?: any) {
    const normalizedStatus = statusSief?.trim().toUpperCase();
    let novoEstado: EstadoNotaEletronica;

    switch (normalizedStatus) {
      case 'EM_DEFINICAO':
        novoEstado = EstadoNotaEletronica.EM_DEFINICAO;
        break;
      case 'AUTORIZADO':
      case 'AUTORIZADA':
        novoEstado = EstadoNotaEletronica.AUTORIZADA;
        break;
      case 'APROVADO':
      case 'APROVADA':
        novoEstado = EstadoNotaEletronica.APROVADO;
        break;
      case 'CANCELADO':
      case 'CANCELADA':
        novoEstado = EstadoNotaEletronica.CANCELADA;
        break;
      case 'DENEGADA':
        novoEstado = EstadoNotaEletronica.DENEGADA;
        break;
      case 'INUTILIZADO':
      case 'INUTILIZADA':
        novoEstado = EstadoNotaEletronica.INUTILIZADA;
        break;
      case 'REJEITADA':
        novoEstado = EstadoNotaEletronica.REJEITADA;
        break;
      case 'ENVIADA':
        novoEstado = EstadoNotaEletronica.ENVIADA;
        break;
      case 'ERRO':
      case 'COM_PROBLEMAS':
        novoEstado = EstadoNotaEletronica.COM_PROBLEMAS;
        break;
      default:
        novoEstado = EstadoNotaEletronica.EM_DEFINICAO;
        break;
    }


    this.estadoNotaEletronica = {
      value: novoEstado,
      label: this.translate.instant(`enums.estado_nota_eletronica.${novoEstado.toLowerCase()}`)
    };

    if (dadosNfe) {
      this.form.mensagemSief = dadosNfe.list?.length > 0 ? dadosNfe.list[0].descMensagem : dadosNfe.descMensagem;
      // Atualizar protocolo se disponivel na resposta.
      // IMPORTANTE: nao sobrescrever dataEmissaoNotaEletronica com dadosNfe.emissao,
      // pois dadosNfe.emissao e a data de criacao do evento de consulta (sempre recente),
      // nao a data real em que a nota foi aprovada no SIFEN.
      // O campo correto ja e carregado do backend em loadNota() via form.dataEmissaoNotaEletronica.
      if (dadosNfe.numeroDoProtocolo) {
        this.form.protocoloNotaEletronica = dadosNfe.numeroDoProtocolo;
      }
    }
  }

  async consultarNfe() {
    if (!this.notaId) {
      console.warn('Nota ID não definido, consulta NFE ignorada.');
      return;
    }

    if (this.isConsultando) return;
    this.isConsultando = true;

    try {
      const response = await firstValueFrom(this.nfeService.getById(`/nfe/consultar`, this.notaId.toString()));
      if (Array.isArray(response) && response.length > 0) {
        const dadosNfe = response[0];
        this.faturaEletronica = dadosNfe;
        this.atualizarEstadoUI(dadosNfe.descMensagem, dadosNfe);
        this.estadoNfeCarregado = true;
      } else if (!this.estadoNfeCarregado) {
        // Nota eletronica sem eventos no SIFEN e sem estado persistido no banco:
        // trata-se de nota nunca transmitida. Mantem o default EM_DEFINICAO
        // (inicializado em buildEstadoNotaEletronica) e libera a UI para
        // exibir o botao "Transmitir".
        this.estadoNfeCarregado = true;
      }
    } catch (error) {
      console.warn('Consulta NFE falhou (suprimido para o usuario):', error);
      // Se o backend falhou ao consultar SIFEN e ainda nao temos estado persistido
      // vindo do banco, assumimos que a nota esta em EM_DEFINICAO (default ja aplicado
      // em buildEstadoNotaEletronica) para nao bloquear o botao "Transmitir" de notas
      // que nunca foram transmitidas. Se a nota ja foi transmitida, loadNota() ja
      // teria populado o estado e a flag, entao este fallback nao sobrescreve nada.
      if (!this.estadoNfeCarregado) {
        this.estadoNfeCarregado = true;
      }
    } finally {
      this.isConsultando = false;
    }
  }

  async consultarNfeComDialogo() {
    if (!this.notaId) return;
    if (this.isConsultando) return;
    this.isConsultando = true;

    this.abrirDialogoOperacaoNfe('consulta');

    this.operacaoNfeProgresso = 25;
    await this.delay(this.PROGRESS_BAR_STEP_DELAY);
    this.operacaoNfeProgresso = 50;

    try {
      const response = await firstValueFrom(this.nfeService.getById(`/nfe/consultar`, this.notaId.toString()));

      this.operacaoNfeProgresso = 75;

      if (Array.isArray(response) && response.length > 0) {
        const dadosNfe = response[0];
        this.faturaEletronica = dadosNfe;
        this.atualizarEstadoUI(dadosNfe.descMensagem, dadosNfe);
      }

      this.finalizarOperacaoNfeSucesso();
      // Abre o dialogo de detalhes apos o auto-close do dialogo de sucesso (2s + margem de 100ms)
      setTimeout(() => {
        this.dialogoDetalhesFaturaVisivel = true;
      }, 2100);
    } catch (error: any) {
      const mensagemErro = error?.error?.message || error?.message || this.translate.instant('fatura-view.mensagens.erro_consultar_nfe');
      this.finalizarOperacaoNfeErro(mensagemErro);
      console.error('Erro na consulta NFE:', error);
    } finally {
      this.isConsultando = false;
    }
  }

  async abrirDialogoDetalhesFaturaVisivel() {
    await this.consultarNfe();
    this.dialogoDetalhesFaturaVisivel = true;
  }

  /**
   * Retorna a data de referencia para calculos de prazo de cancelamento (72h).
   * Usa form.dataEmissaoNotaEletronica, que e o campo persistido no banco e
   * carregado corretamente pelo loadNota(). NAO usa faturaEletronica.emissao,
   * pois esse campo representa a data de criacao do evento de consulta (sempre
   * recente), nao a data real da aprovacao original da nota.
   */
  private obterDataReferenciaAprovacao(): Date | null {
    if (this.form?.dataEmissaoNotaEletronica) {
      const data = new Date(this.form.dataEmissaoNotaEletronica);
      if (!isNaN(data.getTime())) {
        return data;
      }
    }
    return null;
  }

  periodoCancelamentoExpirou(): boolean {
    const estadosAprovados = [
      EstadoNotaEletronica.AUTORIZADA,
      EstadoNotaEletronica.APROVADO,
      EstadoNotaEletronica.ENVIADA
    ];

    if (!estadosAprovados.includes(this.estadoNotaEletronica.value)) {
      return false;
    }

    const dataEmissao = this.obterDataReferenciaAprovacao();
    if (!dataEmissao) {
      return false;
    }

    const agora = new Date();
    const diferencaEmHoras = (agora.getTime() - dataEmissao.getTime()) / (1000 * 60 * 60);

    return diferencaEmHoras > 72;
  }

  notaCanceladaOuInutilizada(): boolean {
    return this.estadoNotaEletronica.value === EstadoNotaEletronica.CANCELADA ||
           this.estadoNotaEletronica.value === EstadoNotaEletronica.INUTILIZADA;
  }

  // ALTO-02: verifica se a nota nao pode receber devolucao (cancelada, inutilizada ou totalmente devolvida)
  devolucaoNaoPermitida(): boolean {
    return this.notaCanceladaOuInutilizada() ||
           this.form.cancelado ||
           this.form.estadoNota === 'DEVOLVIDO';
  }

  /**
   * Calcula a quantidade total devolvida de um item, somando todas as devoluções ativas (não canceladas).
   */
  quantidadeDevolvida(itemId: number | undefined): number {
    if (!itemId || !this.form.listaNotasDevolvidas?.length) return 0;
    return this.form.listaNotasDevolvidas
      .filter(dev => !dev.cancelado)
      .reduce((total, dev) => {
        const itemDev = dev.itemDeNota.find(i => (i.item_id ?? i.item?.id) === itemId);
        return total + (itemDev ? Number(itemDev.quantidade) : 0);
      }, 0);
  }

  /**
   * Retorna o saldo disponível de um item (quantidade original - quantidade devolvida).
   */
  saldoDisponivel(item: any, index: number): number {
    const itemId = item.item_id ?? this.itensDeNota[index]?.id;
    return Number(item.quantidade) - this.quantidadeDevolvida(itemId);
  }

  /** Verifica se a nota ja foi transmitida e pode ser consultada no SIFEN */
  podeConsultarNaSifen(): boolean {
    const estadosConsultaveis = [
      EstadoNotaEletronica.AUTORIZADA,
      EstadoNotaEletronica.APROVADO,
      EstadoNotaEletronica.CANCELADA,
      EstadoNotaEletronica.INUTILIZADA,
      EstadoNotaEletronica.ENVIADA,
    ];
    return estadosConsultaveis.includes(this.estadoNotaEletronica.value);
  }

  podeCancelarNfe(): boolean {
    if (this.notaCancelada) return false;

    const estadosPermitidos = [
      EstadoNotaEletronica.AUTORIZADA,
      EstadoNotaEletronica.APROVADO,
      EstadoNotaEletronica.ENVIADA
    ];

    if (!estadosPermitidos.includes(this.estadoNotaEletronica.value)) {
      return false;
    }

    const dataEmissao = this.obterDataReferenciaAprovacao();
    if (!dataEmissao) {
      return false;
    }

    const agora = new Date();
    const diferencaEmHoras = (agora.getTime() - dataEmissao.getTime()) / (1000 * 60 * 60);

    // Permite cancelamento apenas se a diferenca for menor ou igual a 72 horas
    return diferencaEmHoras <= 72;
  }

  private mapNotaResponse(response: any): NotaDTO {
    return {
      id: response.id,
      emissao: response.emissao,
      emissaoFatura: response.emissaoFatura,
      dataEmissaoNotaEletronica: response.dataEmissaoNotaEletronica,
      codigoNota: response.codigoNota,
      // MEDIO-01: backend pode retornar tipoDeTransacao como string direta ("VENDA")
      // ou como objeto { enum: "VENDA" }. Tratar ambos os casos com fallback seguro.
      tipoDeTransacao: typeof response.tipoDeTransacao === 'string'
        ? response.tipoDeTransacao
        : response.tipoDeTransacao?.enum,
      ehFaturaLegal: response.ehFaturaLegal,
      numeroFatura: response.numeroFatura ?? 0,
      numeroPontoExpedicao: response.numeroPontoExpedicao ?? 0,
      valorCotacaoParaGuarani: response.valorCotacaoParaGuarani,
      valorTotalEmGuarani: response.valorTotalEmGuarani ?? 0,
      numeroFilial: response.numeroFilial ?? 0,
      numeroTimbrado: response.numeroTimbrado ?? '',
      chaveNotaEletronica: response.chaveNotaEletronica ?? '',
      valorTotalEmServicos: response.valorTotalEmServicos,
      valorTotalEmMercadorias: response.valorTotalEmMercadorias,
      timbrado_id: response.timbrado?.id ?? 0,
      tipoNota: response.tipoNota?.enum,
      condicaoDePagamento: response.condicaoDePagamento?.enum,
      conta_id: response.conta?.id ?? 0,
      nomeConta: response.conta?.tituloDaConta ?? '',
      moedaDaConta: response.moedaDaConta?.enum,
      cotacao_id: response.cotacao?.id ?? 0,
      custoTotalDosItens: response.custoTotalDosItens,
      valorTotal: response.valorTotal,
      valorTotalComDesconto: response.valorTotalComDesconto,
      valorDoDesconto: response.valorDoDesconto,
      tipoDeDesconto: response.tipoDeDesconto?.enum,
      porcentagemDesconto: response.porcentagemDesconto,
      valorRecebidoNaConta: response.valorRecebidoNaConta ?? 0,
      valorRecebidoEmGuarani: response.valorRecebidoEmGuarani ?? 0,
      valorRecebidoEmDolar: response.valorRecebidoEmDolar ?? 0,
      valorRecebidoEmReal: response.valorRecebidoEmReal ?? 0,
      valorRecebidoEmPeso: response.valorRecebidoEmPeso ?? 0,
      informacaoDaTransacao: response.informacaoDaTransacao,
      moeda: response.moeda?.enum,
      valorBASEIVA10: response.valorBASEIVA10,
      valorIVA10: response.valorIVA10,
      valorBASEIVA5: response.valorBASEIVA5,
      valorIVA5: response.valorIVA5,
      valorBASEIVA0: response.valorBASEIVA0,
      valorIVA0: response.valorIVA0,
      valorTotalDoImposto: response.valorTotalDoImposto,
      cancelado: response.cancelado,
      // Sem o motivo o usuario ve apenas o badge "Cancelada" e nao sabe por que a nota foi cancelada.
      motivoDeCancelamento: response.motivoDeCancelamento ?? undefined,
      dataDeCancelamento: response.dataDeCancelamento ?? undefined,
      observacao: response.observacao ?? '',
      empresaId: response.empresaId,
      criadoEm: response.criadoEm,
      atualizadoEm: response.atualizadoEm,
      pessoa_id: response.pessoa?.id ?? 0,
      nomePessoa: response.nomePessoa ?? '',
      vendedor_id: response.vendedor?.id ?? 0,
      nomeVendedor: response.nomeVendedor ?? '',
      itemDeNota: response.itemDeNota ?? [],
      baixas: response.baixas ?? [],
      titulos: (response.titulos ?? []).map((t: any) => ({
        ...t,
        moeda: t.moeda?.enum ?? t.moeda
      })),
      numeroParcelas: response.numeroParcelas ?? 0,
      usuarioDeAdicao: response.usuarioDeAdicao,
      usuarioDeUltimaAlteracao: response.usuarioDeUltimaAlteracao,
      notaDevolvida_id: response.notaDevolvida_id,
      // ALTO-02: mapear estadoNota para exibir na UI (EFETIVADO, DEVOLVIDO_PARCIAL, DEVOLVIDO)
      estadoNota: typeof response.estadoNota === 'string'
        ? response.estadoNota
        : response.estadoNota?.enum,
      // Mapear lista de devoluções para calcular quantidades devolvidas por item
      listaNotasDevolvidas: (response.listaNotasDevolvidas ?? []).map((dev: any) => ({
        id: dev.id,
        cancelado: dev.cancelado ?? false,
        itemDeNota: dev.itemDeNota ?? [],
      })),
      protocoloNotaEletronica: response.protocoloNotaEletronica ?? '',

    };
  }

  private mapTimbrado(item: any): TimbradoDTO {
    return {
      id: item.id,
      codigoTimbrado: item.codigoTimbrado,
      timbrado: item.timbrado,
      tipoNota: item.tipoNota?.enum as TipoPadraoNota,
      nomeTipoNota: item.tipoNota?.nome,
      tipoDeTransacao: item.tipoDeTransacao?.enum as TipoTransacao,
      autorizacao: item.autorizacao,
      dataInicio: item.dataInicio,
      dataFim: item.dataFim,
      pontoDeExpedicao: item.pontoDeExpedicao,
      numeroFilial: item.numeroFilial,
      proximoNumero: item.proximoNumero,
      numeroMaximo: item.numeroMaximo,
      criadoEm: item.criadoEm,
      atualizadoEm: item.atualizadoEm,
      empresaId: item.empresaId,
      usuarioDeAdicao: item.usuarioDeAdicao,
      usuarioDeUltimaAlteracao: item.usuarioDeUltimaAlteracao,
    };
  }

  private createEmptyForm(): NotaDTO {
    return {
      id: 0,
      emissao: '',
      emissaoFatura: '',
      codigoNota: 0,
      tipoDeTransacao: TipoTransacao.VENDA,
      ehFaturaLegal: false,
      numeroFatura: 0,
      numeroPontoExpedicao: 0,
      valorCotacaoParaGuarani: 0,
      valorTotalEmGuarani: 0,
      numeroFilial: 0,
      numeroTimbrado: '',
      chaveNotaEletronica: '',
      valorTotalEmServicos: 0,
      valorTotalEmMercadorias: 0,
      timbrado_id: 0,
      tipoNota: TipoPadraoNota.MANUAL,
      condicaoDePagamento: CondicaoDePagamento.CREDITO,
      conta_id: 0,
      nomeConta: '',
      moedaDaConta: Moeda.GUARANI,
      cotacao_id: 0,
      custoTotalDosItens: 0,
      valorTotal: 0,
      valorTotalComDesconto: 0,
      valorDoDesconto: 0,
      tipoDeDesconto: TipoDeDesconto.SEM_DESCONTO,
      porcentagemDesconto: 0,
      valorRecebidoNaConta: 0,
      valorRecebidoEmGuarani: 0,
      valorRecebidoEmDolar: 0,
      valorRecebidoEmReal: 0,
      valorRecebidoEmPeso: 0,
      informacaoDaTransacao: '',
      moeda: Moeda.GUARANI,
      valorBASEIVA10: 0,
      valorIVA10: 0,
      valorBASEIVA5: 0,
      valorIVA5: 0,
      valorBASEIVA0: 0,
      valorIVA0: 0,
      valorTotalDoImposto: 0,
      cancelado: false,
      observacao: '',
      empresaId: undefined,
      criadoEm: '',
      atualizadoEm: '',
      pessoa_id: 0,
      nomePessoa: '',
      vendedor_id: 0,
      nomeVendedor: '',
      itemDeNota: [],
      baixas: [],
      titulos: [],
      numeroParcelas: 0,
      usuarioDeAdicao: undefined,
      usuarioDeUltimaAlteracao: undefined,
      notaDevolvida_id: undefined,
      estadoNota: undefined,
      listaNotasDevolvidas: [],
      protocoloNotaEletronica: '',
      mensagemSief: '',
    };
  }

  private createEmptyItem(): ItemDTO {
    return {
      id: 0,
      codigoItem: 0,
      barras: '',
      nome: '',
      marca_id: null,
      unidadeMedidaDeItem_id: 0,
      precoDeVenda: 0,
      tipoDeImposto: TipoDeImposto.IVA_ISENTO,
      custo: 0,
      grupo_id: null,
      subGrupo_id: null,
      estoqueMinimo: 0,
      tipoDeDesconto: TipoDeDesconto.SEM_DESCONTO,
      desconto: 0,
      fornecedorPrincipal_id: 0,
      status: true,
      saldo: 0,
      imagem: '',
      empresaId: 0,
      criadoEm: null,
      atualizadoEm: '',
      usuarioDeAdicao: 0,
      usuarioDeUltimaAlteracao: null,
      tipoItem: TipoItem.SERVICO
    };
  }

  // =====================================
  // Dialogo de status de operacao NFe
  // =====================================

  private abrirDialogoOperacaoNfe(tipo: 'transmissao' | 'cancelamento' | 'consulta' | 'inutilizacao'): void {
    this.operacaoNfeTipo = tipo;
    this.operacaoNfeEstado = 'processando';
    this.operacaoNfeMensagemErro = '';
    this.operacaoNfeProgresso = 0;
    this.gruposErroNfe = [];
    this.temErrosAgrupados = false;
    this.dialogoOperacaoNfeVisivel = true;
  }

  private finalizarOperacaoNfeSucesso(): void {
    this.operacaoNfeEstado = 'sucesso';
    this.operacaoNfeProgresso = 100;

    // Auto-fechar apos 2 segundos e recarregar nota
    setTimeout(() => {
      this.dialogoOperacaoNfeVisivel = false;
      if (this.notaId) {
        this.loadNota(this.notaId);
      }
    }, 2000);
  }

  private finalizarOperacaoNfeErro(mensagem: string): void {
    this.operacaoNfeEstado = 'erro';
    this.operacaoNfeMensagemErro = mensagem;
    this.operacaoNfeProgresso = 0;
  }

  fecharDialogoOperacaoNfe(): void {
    this.dialogoOperacaoNfeVisivel = false;
    this.operacaoNfeMensagemErro = '';
  }

  // Navega para a pagina de correcao do erro (nova aba ou scroll)
  navegarParaCorrecao(acao: AcaoErroNfe): void {
    if (acao.tipo === 'nova-aba' && acao.rota) {
      const url = this.router.serializeUrl(
        this.router.createUrlTree([acao.rota], { queryParams: acao.queryParams })
      );
      window.open(url, '_blank');
    } else if (acao.tipo === 'scroll' && acao.sectionId) {
      this.fecharDialogoOperacaoNfe();
      setTimeout(() => {
        document.getElementById(acao.sectionId!)?.scrollIntoView({ behavior: 'smooth' });
      }, 300);
    }
  }

  // Agrupa erros de transmissao NFe por grupo e gera acoes de redirecionamento
  private agruparErrosNfe(errors: any[]): GrupoErroNfe[] {
    const grupos: GrupoErroNfe[] = [];
    const grupoMap = new Map<string, any[]>();

    // 1. Agrupar erros por campo 'grupo'
    for (const erro of errors) {
      const grupo = erro.grupo || 'outros';
      if (!grupoMap.has(grupo)) {
        grupoMap.set(grupo, []);
      }
      grupoMap.get(grupo)!.push(erro);
    }

    // 2. Processar cada grupo
    grupoMap.forEach((erros, grupoKey) => {
      if (grupoKey === 'configuracaoEmpresa') {
        this.processarGrupoConfiguracaoEmpresa(erros, grupos);
      } else if (grupoKey === 'pessoa') {
        this.processarGrupoPessoa(erros, grupos);
      } else if (grupoKey === 'timbrado') {
        this.processarGrupoTimbrado(erros, grupos);
      } else if (grupoKey === 'item_de_nota') {
        this.processarGrupoItemDeNota(erros, grupos);
      }
    });

    return grupos;
  }

  // Sub-agrupamento do grupo configuracaoEmpresa
  private processarGrupoConfiguracaoEmpresa(erros: any[], grupos: GrupoErroNfe[]): void {
    const errosAtividade: ErroNfeItem[] = [];
    const errosDte: ErroNfeItem[] = [];

    for (const erro of erros) {
      if (CAMPOS_ATIVIDADE_ECONOMICA.includes(erro.campo)) {
        errosAtividade.push({ campo: erro.campo, mensagem: erro.mensagem });
      } else {
        // Todos os demais campos de configuracaoEmpresa (incluindo codigoEstabelecimento) vao para DTE
        errosDte.push({ campo: erro.campo, mensagem: erro.mensagem });
      }
    }

    // Grupo DTE (certificado, CSC, codigoEstabelecimento, etc)
    if (errosDte.length > 0) {
      grupos.push({
        grupo: 'configuracaoEmpresa',
        titulo: this.translate.instant('fatura.dialogo_operacao_nfe.grupo_configuracao_empresa'),
        erros: errosDte,
        acoes: [{
          tipo: 'nova-aba',
          rota: '/configuracoes-da-empresa',
          queryParams: { tab: 'dte' },
          labelKey: 'fatura.dialogo_operacao_nfe.btn_ir_configuracoes_empresa_dte',
          icone: 'pi pi-cog',
        }],
      });
    }

    // Grupo Atividade Economica
    if (errosAtividade.length > 0) {
      grupos.push({
        grupo: 'configuracaoEmpresaAtividade',
        titulo: this.translate.instant('fatura.dialogo_operacao_nfe.grupo_configuracao_empresa_atividade'),
        erros: errosAtividade,
        acoes: [{
          tipo: 'nova-aba',
          rota: '/configuracoes-da-empresa',
          queryParams: { tab: 'atividade-economica' },
          labelKey: 'fatura.dialogo_operacao_nfe.btn_ir_atividade_economica',
          icone: 'pi pi-briefcase',
        }],
      });
    }
  }

  // Grupo pessoa: redireciona para /clientes ou /fornecedores conforme tipo de transacao
  private processarGrupoPessoa(erros: any[], grupos: GrupoErroNfe[]): void {
    const erroItems: ErroNfeItem[] = erros.map(e => ({ campo: e.campo, mensagem: e.mensagem }));
    const ehVendaOuDevolucao = this.ehTipoDeTransacaoVenda || this.ehTipoDeTransacaoDevolucao;

    grupos.push({
      grupo: 'pessoa',
      titulo: this.translate.instant(
        ehVendaOuDevolucao
          ? 'fatura.dialogo_operacao_nfe.grupo_pessoa_cliente'
          : 'fatura.dialogo_operacao_nfe.grupo_pessoa_fornecedor'
      ),
      erros: erroItems,
      acoes: [{
        tipo: 'nova-aba',
        rota: ehVendaOuDevolucao ? '/clientes' : '/fornecedores',
        labelKey: ehVendaOuDevolucao
          ? 'fatura.dialogo_operacao_nfe.btn_ir_cadastro_cliente'
          : 'fatura.dialogo_operacao_nfe.btn_ir_cadastro_fornecedor',
        icone: 'pi pi-user',
      }],
    });
  }

  // Grupo timbrado: separa numero_fatura_not_null (scroll) dos demais (nova aba)
  private processarGrupoTimbrado(erros: any[], grupos: GrupoErroNfe[]): void {
    const errosNovaAba: ErroNfeItem[] = [];
    const errosScroll: ErroNfeItem[] = [];

    for (const erro of erros) {
      if (erro.campo === CAMPO_NUMERO_FATURA) {
        errosScroll.push({ campo: erro.campo, mensagem: erro.mensagem });
      } else {
        errosNovaAba.push({ campo: erro.campo, mensagem: erro.mensagem });
      }
    }

    if (errosNovaAba.length > 0) {
      grupos.push({
        grupo: 'timbrado',
        titulo: this.translate.instant('fatura.dialogo_operacao_nfe.grupo_timbrado'),
        erros: errosNovaAba,
        acoes: [{
          tipo: 'nova-aba',
          rota: '/timbrado',
          labelKey: 'fatura.dialogo_operacao_nfe.btn_ir_timbrado',
          icone: 'pi pi-file',
        }],
      });
    }

    if (errosScroll.length > 0) {
      grupos.push({
        grupo: 'timbradoFatura',
        titulo: this.translate.instant('fatura.dialogo_operacao_nfe.grupo_timbrado'),
        erros: errosScroll,
        acoes: [{
          tipo: 'scroll',
          sectionId: 'secao-timbrado-fatura',
          labelKey: 'fatura.dialogo_operacao_nfe.btn_ir_timbrado_fatura',
          icone: 'pi pi-arrow-down',
        }],
      });
    }
  }

  // Grupo item_de_nota: sempre faz scroll para a secao de itens
  private processarGrupoItemDeNota(erros: any[], grupos: GrupoErroNfe[]): void {
    grupos.push({
      grupo: 'item_de_nota',
      titulo: this.translate.instant('fatura.dialogo_operacao_nfe.grupo_item_de_nota'),
      erros: erros.map(e => ({ campo: e.campo, mensagem: e.mensagem })),
      acoes: [{
        tipo: 'scroll',
        sectionId: 'secao-itens-nota',
        labelKey: 'fatura.dialogo_operacao_nfe.btn_ir_itens',
        icone: 'pi pi-arrow-down',
      }],
    });
  }

  private showSuccess(detail: string): void {
    this.messageService.add({ severity: 'success', summary: this.translate.instant('common.sucesso'), detail });
  }

  private showError(detail: string): void {
    this.messageService.add({ severity: 'error', summary: this.translate.instant('common.erro'), detail });
  }

  private openPdfFromBase64(base64: string): void {
    try {
      const byteCharacters = atob(base64);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      window.open(url);
    } catch (e) {
      this.showError(this.translate.instant('fatura-view.mensagens.erro_processar_pdf'));
    }
  }

  abrirDialogoDevolucao() {
    if (this.devolucaoNaoPermitida()) {
      this.avisarNotaCancelada();
      return;
    }

    if (!this.form.itemDeNota || this.form.itemDeNota.length === 0 || !this.itensDeNota || this.itensDeNota.length === 0) {
      this.showError(this.translate.instant('fatura-view.mensagens.nenhum_item_devolucao'));
      return;
    }

    // MEDIO-05: lazy loading - so recarregar timbrados se a lista estiver vazia
    if (this.timbradosDevolucaoList.length === 0) {
      this.loadTimbrados(TipoTransacao.DEVOLUCAO);
    }

    // monta itensDeNotaDevolucao fazendo o match pelo item.id === itemDeNota.item_id
    this.itensDeNotaDevolucao = this.itensDeNota.map((item) => {
      // procura o ItemDeNotaDTO correspondente
      const itemDeNota = this.form.itemDeNota.find(i => i.item_id === item.id);
      const quantidade = Number(itemDeNota?.quantidade ?? 0);
      const valorUnitario = Number(itemDeNota?.valorUnitarioComDesconto ?? itemDeNota?.valorUnitario ?? 0);
      const desconto = 0;
      // Calcular saldo disponível considerando devoluções anteriores ativas
      const jaDevolvido = this.quantidadeDevolvida(item.id);
      const saldo = Math.max(0, quantidade - jaDevolvido);

      return {
        ...item,
        quantidade: 0, // inicia zerado para o usuario escolher
        quantidadeMaxima: saldo,
        valorUnitario,
        desconto
      } as ItemDevolucao;
    });

    this.dialogoDevolverEtapa1Visivel = true;
  }

  abrirDialogoFaturaLegal() {
    if (this.notaCancelada) {
      this.avisarNotaCancelada();
      return;
    }

    // MEDIO-05: lazy loading - so recarregar timbrados se a lista estiver vazia
    if ((this.ehTipoDeTransacaoVenda || this.ehTipoDeTransacaoDevolucao) && this.timbradosVendaList.length === 0) {
      this.loadTimbrados(TipoTransacao.VENDA);
    }
    this.dialogoFaturaLegalVisivel = true;
  }

  // abrirDialogoDetalhesFatura() {
  //   this.dialogoDetalhesFaturaVisivel = true;
  // }

  abrirUrlConsulta() {
    if (this.faturaEletronica?.urlNFe) {
      window.open(this.faturaEletronica.urlNFe, '_blank');
    }
  }

  abrirDialogoImpressaoNfe(): void {
    this.dialogoImpressaoNfeVisivel = true;
  }

  async imprimirNotaEletronica() {
    // MEDIO-04: usar comparacao direta com enum ao inves de .includes() fragil
    const estadosPermitidosImpressao = [EstadoNotaEletronica.AUTORIZADA, EstadoNotaEletronica.APROVADO];
    if (!estadosPermitidosImpressao.includes(this.estadoNotaEletronica.value)) {
      this.showError(this.translate.instant('fatura-view.mensagens.nfe_nao_autorizada_impressao'));
      return;
    }

    const tipoImpressao = this.tipoDeImpressaoForm.value?.impressao || 'A4';
    this.isImprimindoNfe = true;

    try {
      const response = await firstValueFrom(
        this.nfeService.imprimirNfe(this.notaId!, tipoImpressao)
      );

      if (response?.pdfBase64) {
        this.openPdfFromBase64(response.pdfBase64);
      } else {
        this.showError(this.translate.instant('fatura-view.mensagens.erro_carregar_pdf_nfe'));
      }
    } catch (error) {
      console.error(error);
      this.showError(this.translate.instant('fatura-view.mensagens.erro_imprimir_nfe'));
    } finally {
      this.isImprimindoNfe = false;
    }
  }

  async downloadNotaEletronica() {
    // MEDIO-04: usar comparacao direta com enum ao inves de .includes() fragil
    const estadosPermitidosDownload = [EstadoNotaEletronica.AUTORIZADA, EstadoNotaEletronica.APROVADO];
    if (!estadosPermitidosDownload.includes(this.estadoNotaEletronica.value)) {
      this.showError(this.translate.instant('fatura-view.mensagens.nfe_nao_autorizada_download'));
      return;
    }

    const tipoImpressao = this.tipoDeImpressaoForm.value?.impressao || 'A4';
    this.isImprimindoNfe = true;

    try {
      const response = await firstValueFrom(
        this.nfeService.imprimirNfe(this.notaId!, tipoImpressao)
      );

      if (response?.pdfBase64) {
        this.downloadPdfFromBase64(response.pdfBase64, response.nomeArquivo);
      } else {
        this.showError(this.translate.instant('fatura-view.mensagens.erro_carregar_pdf_nfe'));
      }
    } catch (error) {
      console.error(error);
      this.showError(this.translate.instant('fatura-view.mensagens.erro_baixar_nfe'));
    } finally {
      this.isImprimindoNfe = false;
    }
  }

  private downloadPdfFromBase64(base64: string, nomeArquivo: string): void {
    try {
      const byteCharacters = atob(base64);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = nomeArquivo || `nota-eletronica-${this.notaId}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      this.showError(this.translate.instant('fatura-view.mensagens.erro_processar_pdf'));
    }
  }

  calcularValorUnitarioItemDevolucaoComDesconto(item: ItemDevolucao): number {
    // item.valorUnitario ja e o valor liquido (= valorUnitarioComDesconto da nota original),
    // pois o setup do dialog forca desconto=0 e valorUnitario=valorUnitarioComDesconto.
    // A formula anterior (valorUnitario - desconto) era uma bomba latente: se desconto
    // voltasse a ser nao-zero o calculo duplicaria o abatimento. Retornar diretamente.
    return item.valorUnitario;
  }

  /**
   * Calcula o valor a ser enviado em `valorRecebidoNaConta` para pagamento via conta
   * na nota de devolucao.
   *
   * Regra (alinhada com backend que grava Baixa.valor direto sem reconverter):
   * - Os valores monetarios (totalDevolucao, valorTotal, etc.) estao SEMPRE na
   *   moeda padrao da empresa (this.moedaPadraoISO -> isoToMoeda), nao na moeda
   *   da nota original.
   * - Se a moeda da conta == moeda padrao da empresa, envia o valor tal qual.
   * - Se diferem, converte da moeda padrao da empresa para a moeda da conta.
   */
  private calcularValorRecebidoNaConta(
    valorTotal: number,
    moedaConta: string | undefined,
  ): number {
    const moedaEmpresa = this.isoToMoeda(this.moedaPadraoISO) || Moeda.GUARANI;
    if (!moedaConta || moedaConta === moedaEmpresa) {
      return valorTotal;
    }
    const origemIso = enumMoedaParaIso(moedaEmpresa);
    const destinoIso = enumMoedaParaIso(moedaConta);
    if (!origemIso || !destinoIso) {
      return valorTotal; // fallback defensivo — moeda desconhecida
    }
    // Conversao DIRETA par-a-par (espelha ConversorMoedasUtil.kt do backend).
    return converterDireto(valorTotal, origemIso, destinoIso, {
      cotPYG: this.cotPYG,
      cotBRL: this.cotBRL,
      cotRealGuarani: this.cotRealGuarani,
      cotPesoGuarani: this.cotPesoGuarani,
    });
  }

  calcularValorTotalItensDeNotaDevolucao(): number {
    if (!this.itensDeNotaDevolucao || this.itensDeNotaDevolucao.length === 0) return 0;

    // item.valorUnitario ja e liquido; desconto e sempre 0 apos o fix do dialog.
    // Remover `- desconto` elimina a bomba latente de duplo abatimento.
    return this.itensDeNotaDevolucao.reduce((total, item) => {
      const quantidade = Number(item.quantidade ?? 0);
      const valorUnitario = Number(item.valorUnitario ?? 0);
      return total + quantidade * valorUnitario;
    }, 0);
  }

  private formatarNumeroBaseFatura(numeroFilial?: number, pontoDeExpedicao?: number, numeroFatura?: number): string {
    if (numeroFilial == null || pontoDeExpedicao == null || numeroFatura == null) {
      return '';
    }

    const filial = String(numeroFilial).padStart(3, '0');
    const expedicao = String(pontoDeExpedicao).padStart(3, '0');
    const fatura = String(numeroFatura).padStart(7, '0');

    return `${filial}-${expedicao}-${fatura}`;
  }

  formatarNumeroTimbradoFatura(timbrado?: TimbradoDTO): string {
    if (!timbrado) return '';
    return this.formatarNumeroBaseFatura(
      timbrado.numeroFilial,
      timbrado.pontoDeExpedicao,
      this.form.numeroFatura
    );
  }

  formatarNumeroFaturaCompra(): string {
    return this.formatarNumeroBaseFatura(
      this.form.numeroFilial,
      this.form.numeroPontoExpedicao,
      this.form.numeroFatura
    );
  }

  formatarNumeroTimbrado(timbrado?: TimbradoDTO): string {
    if (!timbrado) return '';
    return this.formatarNumeroBaseFatura(
      timbrado.numeroFilial,
      timbrado.pontoDeExpedicao,
      timbrado.proximoNumero
    );
  }

  formatarData(dataISO: string): string {
    const data = new Date(dataISO);
    if (isNaN(data.getTime())) {
      return '';
    }
    const dia = String(data.getDate()).padStart(2, '0');
    const mes = String(data.getMonth() + 1).padStart(2, '0');
    const ano = data.getFullYear();
    return `${dia}/${mes}/${ano}`;
  }

  formatarDataEHora(dataISO: string): string {
    const data = new Date(dataISO);
    if (isNaN(data.getTime())) {
      return '';
    }
    const dia = String(data.getDate()).padStart(2, '0');
    const mes = String(data.getMonth() + 1).padStart(2, '0');
    const ano = data.getFullYear();
    const horas = String(data.getHours()).padStart(2, '0');
    const minutos = String(data.getMinutes()).padStart(2, '0');
    return `${dia}/${mes}/${ano} ${horas}:${minutos}`;
  }

  /**
   * Observacao do item de nota para exibicao, sem os valores de preenchimento obrigatorio e sem o
   * trecho de variantes/modificadores (ja listados abaixo). Ver `observacaoItemParaExibicao`.
   */
  /**
   * Quantidade da variante/modificador em CADA unidade do item. A gravada e o total da linha e so
   * repetia a quantidade do item; a tela mostra "(xN)" apenas quando vai mais de uma por unidade.
   */
  quantidadePorUnidade(quantidade: number | null | undefined, quantidadeDoItem: number | null | undefined): number {
    const total = Number(quantidade) || 0;
    const itens = Number(quantidadeDoItem) || 0;
    return itens > 0 ? Math.round(total / itens) : total;
  }

  observacaoVisivelDoItem(item: any, nomeItem?: string): string {
    return observacaoItemParaExibicao(item?.observacao, nomeItem);
  }

  formatarValorMonetario(valor: number, moeda: Moeda): string {
    if (valor == null || isNaN(valor)) return '-';

    let locale: string;
    let currencySymbol: string;

    switch (moeda) {
      case Moeda.GUARANI:
        locale = 'es-PY';
        currencySymbol = 'Gs';
        break;
      case Moeda.REAL:
        locale = 'pt-BR';
        currencySymbol = 'R$';
        break;
      case Moeda.DOLAR:
        locale = 'en-US';
        currencySymbol = '$';
        break;
      case Moeda.PESO:
        // ARS delega ao CLDR `es-AR` que produz `$ N,NN`
        // (decisao tech lead 2026-05-06).
        locale = 'es-AR';
        currencySymbol = '$';
        break;
      default:
        locale = 'es-PY';
        currencySymbol = 'Gs';
        break;
    }

    const options: Intl.NumberFormatOptions =
      moeda === Moeda.GUARANI
        ? { minimumFractionDigits: 0, maximumFractionDigits: 0 }
        : { minimumFractionDigits: 2, maximumFractionDigits: 2 };

    const formatado = new Intl.NumberFormat(locale, options).format(valor);
    return `${currencySymbol} ${formatado}`;
  }

  /**
   * Retorna o valor absoluto de um número (tratando null/undefined como 0).
   *
   * Usado no bloco "Método de Pagamento" da fatura para exibir o valor de troco
   * (valorRecebidoEm{Moeda} < 0) sem o sinal de menos, em conjunto com o badge
   * "Troco/Vuelto" (chave i18n: fatura.labels.troco).
   *
   * Não altera a persistência — só a apresentação visual.
   */
  absValor(v: number | null | undefined): number {
    return Math.abs(v ?? 0);
  }

  /**
   * Retorna a chave i18n para o rotulo da moeda informada.
   *
   * Usado no bloco "Método de Pagamento" da fatura, na linha de pagamento
   * em conta, para exibir "Guaraní/Dólar/Real/Peso — {nomeConta}" conforme
   * a moeda da conta cadastrada. Mantém as mesmas chaves já utilizadas nas
   * linhas de pagamento em efetivo logo abaixo (fatura.labels.guarani/dolar/real/peso).
   */
  getMoedaLabel(moeda: Moeda | null | undefined): string {
    switch (moeda) {
      case Moeda.GUARANI:
        return 'fatura.labels.guarani';
      case Moeda.DOLAR:
        return 'fatura.labels.dolar';
      case Moeda.REAL:
        return 'fatura.labels.real';
      case Moeda.PESO:
        return 'fatura.labels.peso';
      default:
        return 'fatura.labels.guarani';
    }
  }

  navigateListaDeNotas(): void {
    if (this.ehTipoDeTransacaoVenda) {
      this.router.navigate(['vendas-list']);
    } else if (this.ehTipoDeTransacaoCompra) {
      this.router.navigate(['compras-list']);
    } else if (this.ehTipoDeTransacaoDevolucao) {
      this.router.navigate(['devolucao-vendas']);
    } else if (this.ehTipoDeTransacaoDevolucaoDeCompra) {
      this.router.navigate(['devolucao-compras']);
    } else {
      this.router.navigate(['notas-list']);
    }
  }

  onImageError(event: Event): void {
    (event.target as HTMLImageElement).src = 'assets/images/profile_placeholder.png';
  }

  ehTipoDeDevolucaoEfetivo(): boolean {
    return this.tipoDeDevolucaoForm.value.tipoDeDevolucao == TipoDeDevolucao.EFETIVO;
  }

  ehTipoDeDevolucaoConta(): boolean {
    return this.tipoDeDevolucaoForm.value.tipoDeDevolucao == TipoDeDevolucao.CONTA;
  }

  // HOTKEYS
  @HostListener('document:keydown', ['$event'])
  handleKeyboardShortcut(event: KeyboardEvent): void {
    // --- Escape: fechar dialogs abertos (prioridade maxima) ou voltar se nenhum dialogo aberto ---
    if (event.key === 'Escape') {
      if (this.algumDialogoAberto()) {
        this.fecharDialogoAtivo(event);
      } else {
        event.preventDefault();
        this.navigateListaDeNotas();
      }
      return;
    }

    // --- Enter: confirmar acao principal do dialogo ativo ---
    if (event.key === 'Enter') {
      if (this.dialogoConfirmarTransmissaoVisivel && !this.isTransmitindo) {
        event.preventDefault();
        this.confirmarTransmissao();
        return;
      }
      if (this.dialogoFaturaLegalVisivel) {
        event.preventDefault();
        this.transformaNotaEmFatura();
        return;
      }
      if (this.dialogoEditarFaturaLegalVisivel) {
        event.preventDefault();
        this.atualizarTimbrado();
        return;
      }
      if (this.dialogoDevolverEtapa1Visivel) {
        event.preventDefault();
        this.devolver();
        return;
      }
      if (this.dialogoMotivoCancelamentoVisivel && this.podeConfirmarCancelamento) {
        event.preventDefault();
        // Roteia conforme o botao que abriu o dialogo (fiscal ou apenas no sistema). Usa a mesma
        // regra do botao: com pagamento a estornar, o Enter tambem espera o aceite.
        this.confirmarCancelamento(this.motivoCancelamentoNfe);
        return;
      }
      if (this.dialogoMotivoInutilizacaoVisivel && this.motivoInutilizacaoNfe && this.motivoInutilizacaoNfe.length >= 5) {
        event.preventDefault();
        this.inutilizarNfe(this.motivoInutilizacaoNfe);
        return;
      }
      if (this.dialogoImpressaoVisivel) {
        event.preventDefault();
        this.imprimir();
        return;
      }
      if (this.dialogoImpressaoNfeVisivel && !this.isImprimindoNfe) {
        event.preventDefault();
        this.imprimirNotaEletronica();
        return;
      }
    }

    // Ignorar atalhos Ctrl quando input/textarea esta focado
    const target = event.target as HTMLElement;
    const isInputFocused = target.tagName === 'INPUT' ||
                            target.tagName === 'TEXTAREA' ||
                            target.isContentEditable;
    if (isInputFocused) return;

    // --- Ctrl + tecla (sem Shift) ---
    // preventDefault e chamado ANTES das verificacoes de negocio para evitar
    // que o browser processe atalhos nativos (ex: Ctrl+P imprime)
    if (event.ctrlKey && !event.shiftKey && !event.altKey) {
      const teclaCtrl = event.key.toLowerCase();
      const teclasGerenciadas = ['p', 'd', 'l', 'h', 'e', 'u'];
      if (teclasGerenciadas.includes(teclaCtrl)) {
        event.preventDefault();
      }

      switch (teclaCtrl) {
        case 'p':
          this.dialogoImpressaoVisivel = true;
          break;
        case 'd':
          if ((this.ehTipoDeTransacaoVenda || this.ehTipoDeTransacaoCompra) && !this.devolucaoNaoPermitida()) {
            this.abrirDialogoDevolucao();
          }
          break;
        case 'l':
          if (!this.form.ehFaturaLegal && !this.notaCancelada) {
            this.abrirDialogoFaturaLegal();
          }
          break;
        case 'h':
          if (this.estadoNotaEletronica.value !== EstadoNotaEletronica.EM_DEFINICAO) {
            this.buscarEventoEnvioNfe();
          }
          break;
        case 'e':
          if (this.form.ehFaturaLegal && !this.notaCancelada) {
            this.dialogoEditarFaturaLegalVisivel = true;
          }
          break;
        case 'u':
          if (this.faturaEletronica?.urlNFe) {
            this.abrirUrlConsulta();
          }
          break;
      }
    }

    // --- Alt + tecla (atalhos que conflitam com o browser em Ctrl) ---
    if (event.altKey && !event.ctrlKey && !event.shiftKey) {
      const teclaAlt = event.key.toLowerCase();
      const teclasGerenciadas = ['t', 'q', 'c', 'i'];
      if (teclasGerenciadas.includes(teclaAlt)) {
        event.preventDefault();
      }

      switch (teclaAlt) {
        case 't':
          if (this.estadoNfeCarregado && !this.notaCancelada &&
              (this.estadoNotaEletronica.value === EstadoNotaEletronica.EM_DEFINICAO ||
              this.estadoNotaEletronica.value === EstadoNotaEletronica.COM_PROBLEMAS)) {
            this.abrirDialogoConfirmarTransmissao();
          }
          break;
        case 'q':
          if (this.estadoNotaEletronica.value === EstadoNotaEletronica.AUTORIZADA ||
              this.estadoNotaEletronica.value === EstadoNotaEletronica.CANCELADA ||
              this.estadoNotaEletronica.value === EstadoNotaEletronica.APROVADO) {
            this.consultarNfeComDialogo();
          }
          break;
        case 'c':
          // Mesmo atalho para os dois casos: com NF-e valida no fisco cancela pelo fluxo
          // fiscal; sem ela, cancela apenas no sistema.
          if (this.podeCancelarNfe() && this.podeCancelarNotaPermissao) {
            this.abrirDialogoCancelamento('nfe');
          } else if (this.podeCancelarNotaSimples()) {
            this.abrirDialogoCancelamento('nota');
          }
          break;
        case 'i':
          if (this.estadoNotaEletronica.value === EstadoNotaEletronica.COM_PROBLEMAS && !this.notaCancelada) {
            this.dialogoMotivoInutilizacaoVisivel = true;
          }
          break;
      }
    }

    // --- Ctrl + Shift + tecla ---
    // preventDefault e chamado ANTES das verificacoes de negocio para evitar
    // que o browser processe atalhos nativos
    if (event.ctrlKey && event.shiftKey && !event.altKey) {
      const teclaCtrlShift = event.key.toLowerCase();
      const teclasGerenciadas = ['p', 'd', 'c'];
      if (teclasGerenciadas.includes(teclaCtrlShift)) {
        event.preventDefault();
      }

      switch (teclaCtrlShift) {
        case 'p':
          if (this.estadoNotaEletronica.value === EstadoNotaEletronica.AUTORIZADA ||
              this.estadoNotaEletronica.value === EstadoNotaEletronica.APROVADO) {
            this.abrirDialogoImpressaoNfe();
          }
          break;
        case 'd':
          if (this.dialogoImpressaoVisivel) {
            this.downloadPDF();
          }
          break;
        case 'c':
          if (this.podeConsultarNaSifen() && this.faturaEletronica?.urlNFe) {
            this.abrirUrlConsulta();
          }
          break;
      }
    }
  }

  private fecharDialogoAtivo(event: KeyboardEvent): void {
    if (this.dialogoOperacaoNfeVisivel && this.operacaoNfeEstado === 'erro') { event.preventDefault(); this.fecharDialogoOperacaoNfe(); return; }
    if (this.dialogoImpressaoNfeVisivel) { event.preventDefault(); this.dialogoImpressaoNfeVisivel = false; return; }
    if (this.dialogoImpressaoVisivel) { event.preventDefault(); this.dialogoImpressaoVisivel = false; return; }
    if (this.dialogoConfirmarTransmissaoVisivel) { event.preventDefault(); this.dialogoConfirmarTransmissaoVisivel = false; return; }
    if (this.dialogoDetalhesFaturaVisivel) { event.preventDefault(); this.dialogoDetalhesFaturaVisivel = false; return; }
    if (this.dialogoHistoricoEventosVisivel) { event.preventDefault(); this.dialogoHistoricoEventosVisivel = false; return; }
    if (this.dialogoMotivoCancelamentoVisivel) { event.preventDefault(); this.dialogoMotivoCancelamentoVisivel = false; return; }
    if (this.dialogoMotivoInutilizacaoVisivel) { event.preventDefault(); this.dialogoMotivoInutilizacaoVisivel = false; return; }
    if (this.dialogoFaturaLegalVisivel) { event.preventDefault(); this.dialogoFaturaLegalVisivel = false; return; }
    if (this.dialogoEditarFaturaLegalVisivel) { event.preventDefault(); this.dialogoEditarFaturaLegalVisivel = false; return; }
    if (this.dialogoDevolverEtapa1Visivel) { event.preventDefault(); this.dialogoDevolverEtapa1Visivel = false; return; }
    if (this.mostrarDialogPagamentoDevolucao) { event.preventDefault(); this.mostrarDialogPagamentoDevolucao = false; return; }
  }

  private algumDialogoAberto(): boolean {
    return this.dialogoOperacaoNfeVisivel ||
      this.dialogoImpressaoVisivel ||
      this.dialogoFaturaLegalVisivel ||
      this.dialogoEditarFaturaLegalVisivel ||
      this.dialogoDevolverEtapa1Visivel ||
      this.dialogoDetalhesFaturaVisivel ||
      this.dialogoHistoricoEventosVisivel ||
      this.dialogoMotivoCancelamentoVisivel ||
      this.dialogoMotivoInutilizacaoVisivel ||
      this.dialogoConfirmarTransmissaoVisivel ||
      this.dialogoImpressaoNfeVisivel ||
      this.mostrarDialogPagamentoDevolucao;
  }

}

